"use client";

import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  GripVertical,
  Eye,
  X,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  FolderPlus,
  Search,
  Check,
  Copy,
  FileSpreadsheet,
  MoveHorizontal,
  LayoutTemplate,
  Download,
  FormInput,
  ShieldCheck,
  ChevronsUpDown,
  HelpCircle,
  Save,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  listAllFormFieldGroups,
  createFormFieldGroup,
  updateFormFieldGroup,
  deleteFormFieldGroup,
  createFormField,
  updateFormField,
  deleteFormField,
  reorderFormFieldGroups,
  reorderFormFields,
  checkFormFieldSlugUsage,
  type SlugUsageResponse,
  type SlugUsage,
} from "@/lib/api/form-fields";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { listAllReferenceTables } from "@/lib/api/references";
import {
  listAllGroupTemplatesWithFields,
  listFieldTemplates,
  applyGroupTemplateToVersion,
  applyFieldTemplateToVersion,
} from "@/lib/api/templates";
import { getErrorMessage } from "@/lib/api/client";
import { slugify, cn, parseBBCode } from "@/lib/utils";
import type { 
  FormField as FormFieldType, 
  FieldType, 
  FieldWidth,
  ConditionOperator, 
  ConditionLogic, 
  VisibilityConditions,
  FormFieldGroupWithFields,
  ReferenceFilter,
  ConditionalConstraints,
  ConditionalRule,
  ConditionalRequired,
  FormFieldGroupTemplateWithFields,
  FormFieldTemplate,
  GroupValidationRules,
  GroupActivationMode,
  LabelStyle,
  LabelFontSize,
  LabelFontWeight,
  LabelFontStyle,
  LabelTextTransform,
  LabelColor,
} from "@/types";

interface FormFieldsTabProps {
  versionId: number;
  isEditable: boolean;
  openFieldSlug?: string | null;
  onOpenFieldSlugConsumed?: () => void;
}

// Field Slug Selector Component with search
interface FieldWithPathOption {
  field: { slug: string; field_type: string; enum_values: string[] | null };
  fullPath: string;
  label: string;
}

interface FieldSlugSelectorProps {
  value: string;
  onSelect: (slug: string) => void;
  fields: FieldWithPathOption[];
  excludeSlug?: string;
  filterTypes?: string[];
  placeholder?: string;
  className?: string;
}

function FieldSlugSelector({ 
  value, 
  onSelect, 
  fields, 
  excludeSlug, 
  filterTypes,
  placeholder = "Sélectionner un champ...",
  className,
}: FieldSlugSelectorProps) {
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  
  // Filter fields based on excludeSlug and filterTypes
  const filteredFields = useMemo(() => {
    return fields.filter((f) => {
      if (excludeSlug && f.field.slug === excludeSlug) return false;
      if (filterTypes && filterTypes.length > 0 && !filterTypes.includes(f.field.field_type)) return false;
      return true;
    });
  }, [fields, excludeSlug, filterTypes]);
  
  // Find the selected field to display its fullPath
  const selectedField = filteredFields.find((f) => f.field.slug === value);
  
  return (
    <Popover 
      open={open} 
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        // Reset tooltip state when popover opens
        if (isOpen) {
          setTooltipOpen(false);
        }
      }}
    >
      <Tooltip 
        open={open ? false : tooltipOpen} 
        onOpenChange={setTooltipOpen}
      >
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn("justify-between font-normal min-w-0", className)}
            >
              {selectedField ? (
                <span className="truncate font-mono text-xs">{selectedField.fullPath}</span>
              ) : (
                <span className="text-muted-foreground truncate">{placeholder}</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        {selectedField && (
          <TooltipContent side="top" className="max-w-md">
            <p className="font-mono text-xs">{selectedField.fullPath}</p>
            <p className="text-xs text-muted-foreground">{selectedField.label}</p>
          </TooltipContent>
        )}
      </Tooltip>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher un champ..." />
          <CommandList>
            <CommandEmpty>Aucun champ trouvé.</CommandEmpty>
            <CommandGroup>
              {filteredFields.map((f) => (
                <CommandItem
                  key={f.field.slug}
                  value={`${f.fullPath} ${f.label}`}
                  onSelect={() => {
                    onSelect(f.field.slug);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === f.field.slug ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <span className="font-mono text-xs truncate">{f.fullPath}</span>
                    <span className="text-xs text-muted-foreground truncate">{f.label}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Sortable Field Component
interface FlatGroupItem {
  group: FormFieldGroupWithFields;
  depth: number;
}

interface SortableFieldProps {
  field: FormFieldType;
  isEditable: boolean;
  onEdit: (field: FormFieldType) => void;
  onDuplicate: (field: FormFieldType) => void;
  onDelete: (id: number) => void;
  onMove: (fieldId: number, newGroupId: number) => void;
  getTypeBadge: (type: FieldType) => React.ReactNode;
  flatGroups: FlatGroupItem[];
}

function SortableField({ field, isEditable, onEdit, onDuplicate, onDelete, onMove, getTypeBadge, flatGroups }: SortableFieldProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `field-${field.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 rounded-lg bg-background"
    >
      <div className="flex items-center gap-3">
        {isEditable && (
          <button
            className="cursor-grab active:cursor-grabbing touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        {!isEditable && <GripVertical className="h-4 w-4 text-muted-foreground opacity-30" />}
        <div>
          <div className="flex items-center gap-2">
            <p 
              className="font-medium cursor-pointer hover:underline hover:text-primary transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(field);
              }}
            >
              {field.label}
            </p>
            {getTypeBadge(field.field_type)}
            {field.required && (
              <Badge variant="destructive" className="text-xs">
                Requis
              </Badge>
            )}
            {field.visibility_conditions && (
              <Badge variant="outline" className="text-xs bg-warning/20 text-warning border-warning/30">
                <Eye className="h-3 w-3 mr-1" />
                Conditionnel
              </Badge>
            )}
            {field.conditional_constraints && (
              <Badge variant="outline" className="text-xs bg-info/20 text-info border-info/30">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 3h5v5" />
                  <path d="M8 3H3v5" />
                  <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" />
                  <path d="m15 9 6-6" />
                </svg>
                Contraintes
              </Badge>
            )}
            {field.width && field.width !== "full" && (
              <Badge variant="outline" className="text-xs bg-success/20 text-success border-success/30">
                {field.width === "half" ? "1/2" : field.width === "third" ? "1/3" : "1/4"}
              </Badge>
            )}
            {field.excel_source && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <FileSpreadsheet className="h-4 w-4 text-success cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-success" />
                    <div>
                      <p className="text-xs text-muted-foreground">Source Excel</p>
                      <p className="font-mono text-sm">{field.excel_source}</p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
            {field.label_style && Object.keys(field.label_style).length > 0 && (
              <Badge variant="outline" className="text-xs bg-purple-500/20 text-purple-600 border-purple-500/30">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 7 4 4 20 4 20 7" />
                  <line x1="9" x2="15" y1="20" y2="20" />
                  <line x1="12" x2="12" y1="4" y2="20" />
                </svg>
                Style
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            <code className="px-1 py-0.5 bg-muted rounded text-xs">
              {field.slug}
            </code>
            {field.description && (
              <span className="ml-2">• {field.description}</span>
            )}
          </p>
        </div>
      </div>

      {isEditable && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(field)}>
              <Pencil className="mr-2 h-4 w-4" />
              Modifier
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDuplicate(field)}>
              <Copy className="mr-2 h-4 w-4" />
              Dupliquer
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <MoveHorizontal className="mr-2 h-4 w-4" />
                Déplacer vers
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
                {flatGroups
                  .filter((item) => item.group.id !== field.group_id)
                  .map(({ group, depth }) => (
                    <DropdownMenuItem
                      key={group.id}
                      onClick={() => onMove(field.id, group.id)}
                    >
                      <span className="text-muted-foreground mr-1">
                        {"─".repeat(depth)}
                      </span>
                      {group.name}
                    </DropdownMenuItem>
                  ))}
                {flatGroups.filter((item) => item.group.id !== field.group_id).length === 0 && (
                  <DropdownMenuItem disabled>
                    Aucun autre groupe disponible
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(field.id)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

// Sortable Group Component
interface SortableGroupProps {
  group: FormFieldGroupWithFields;
  depth: number;
  isEditable: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onEditGroup: (group: FormFieldGroupWithFields) => void;
  onDeleteGroup: (id: number) => void;
  onAddSubGroup: (group: FormFieldGroupWithFields) => void;
  onAddField: (groupId: number) => void;
  onEditField: (field: FormFieldType) => void;
  onDuplicateField: (field: FormFieldType) => void;
  onDeleteField: (id: number) => void;
  onMoveField: (fieldId: number, newGroupId: number) => void;
  getTypeBadge: (type: FieldType) => React.ReactNode;
  onFieldDragEnd: (groupId: number, activeId: string, overId: string) => void;
  onChildGroupDragEnd: (parentId: number, activeId: string, overId: string) => void;
  renderChildGroup: (child: FormFieldGroupWithFields, depth: number) => React.ReactNode;
  expandedGroups: Set<number>;
  onToggleGroup: (groupId: number) => void;
  flatGroups: FlatGroupItem[];
}

function SortableGroup({
  group,
  depth,
  isEditable,
  isExpanded,
  onToggle,
  onEditGroup,
  onDeleteGroup,
  onAddSubGroup,
  onAddField,
  onEditField,
  onDuplicateField,
  onDeleteField,
  onMoveField,
  getTypeBadge,
  onFieldDragEnd,
  onChildGroupDragEnd,
  renderChildGroup,
  expandedGroups,
  onToggleGroup,
  flatGroups,
}: SortableGroupProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `group-${group.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginLeft: depth * 16,
  };

  const hasContent = group.fields.length > 0 || group.children.length > 0;
  const sortedFields = useMemo(
    () => [...group.fields].sort((a, b) => a.order - b.order),
    [group.fields]
  );
  const fieldIds = useMemo(
    () => sortedFields.map((f) => `field-${f.id}`),
    [sortedFields]
  );
  const sortedChildren = useMemo(
    () => [...group.children].sort((a, b) => a.order - b.order),
    [group.children]
  );
  const childGroupIds = useMemo(
    () => sortedChildren.map((c) => `group-${c.id}`),
    [sortedChildren]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onFieldDragEnd(group.id, String(active.id), String(over.id));
    }
  };

  const handleChildGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onChildGroupDragEnd(group.id, String(active.id), String(over.id));
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {isEditable && (
            <button
              className="cursor-grab active:cursor-grabbing touch-none"
              onClick={(e) => e.stopPropagation()}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          {!isEditable && <div className="w-4" />}
          {hasContent ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : (
            <div className="w-4" />
          )}
          <FolderOpen className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{group.name}</p>
              {group.display_context === "save" && (
                <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                  <Save className="h-3 w-3 mr-1" />
                  Enregistrement
                </Badge>
              )}
              {group.visibility_conditions && (
                <Badge variant="outline" className="text-xs bg-warning/20 text-warning border-warning/30">
                  <Eye className="h-3 w-3 mr-1" />
                  Conditionnel
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {group.fields.length} champ{group.fields.length > 1 ? "s" : ""}
              {group.children.length > 0 && `, ${group.children.length} sous-groupe${group.children.length > 1 ? "s" : ""}`}
            </p>
          </div>
        </div>

        {isEditable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onAddField(group.id);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Ajouter un champ
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onAddSubGroup(group);
                }}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                Ajouter un sous-groupe
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEditGroup(group);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteGroup(group.id);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {isExpanded && (
        <div className="border-t px-4 py-2 bg-muted/30 space-y-2">
          {/* Fields - only show section if there are fields OR if there are no children (to show empty state) */}
          {(group.fields.length > 0 || group.children.length === 0) && (
            group.fields.length === 0 && group.children.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Aucun champ dans ce groupe
              </p>
            ) : (
              <>
                {isEditable ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleFieldDragEnd}
                  >
                    <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
                      {sortedFields.map((field) => (
                        <SortableField
                          key={field.id}
                          field={field}
                          isEditable={isEditable}
                          onEdit={onEditField}
                          onDuplicate={onDuplicateField}
                          onDelete={onDeleteField}
                          onMove={onMoveField}
                          getTypeBadge={getTypeBadge}
                          flatGroups={flatGroups}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  sortedFields.map((field) => (
                    <SortableField
                      key={field.id}
                      field={field}
                      isEditable={isEditable}
                      onEdit={onEditField}
                      onDuplicate={onDuplicateField}
                      onDelete={onDeleteField}
                      onMove={onMoveField}
                      getTypeBadge={getTypeBadge}
                      flatGroups={flatGroups}
                    />
                  ))
                )}
              </>
            )
          )}

          {/* Children groups */}
          {group.children.length > 0 && (
            <div className="space-y-2">
              {isEditable ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleChildGroupDragEnd}
                >
                  <SortableContext items={childGroupIds} strategy={verticalListSortingStrategy}>
                    {sortedChildren.map((child) => renderChildGroup(child, depth + 1))}
                  </SortableContext>
                </DndContext>
              ) : (
                sortedChildren.map((child) => renderChildGroup(child, depth + 1))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const fieldTypeLabels: Record<FieldType, string> = {
  text: "Texte",
  number: "Nombre",
  select: "Liste déroulante",
  multiselect: "Sélection multiple",
  checkbox: "Case à cocher",
  date: "Date",
  textarea: "Zone de texte",
  radio: "Boutons radio",
};

const operatorLabels: Record<ConditionOperator, string> = {
  equals: "Égal à",
  not_equals: "Différent de",
  greater_than: "Supérieur à",
  greater_than_or_equals: "Supérieur ou égal à",
  less_than: "Inférieur à",
  less_than_or_equals: "Inférieur ou égal à",
  contains: "Contient",
  not_contains: "Ne contient pas",
  is_empty: "Est vide",
  is_not_empty: "N'est pas vide",
  in: "Dans la liste",
  not_in: "Pas dans la liste",
};

export function FormFieldsTab({ versionId, isEditable, openFieldSlug, onOpenFieldSlugConsumed }: FormFieldsTabProps) {
  const queryClient = useQueryClient();
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingOpenSlug, setPendingOpenSlug] = useState<string | null>(null);
  
  // Queries - need to be before effects that depend on them
  const { data: groups, isLoading } = useQuery({
    queryKey: ["form-field-groups", versionId],
    queryFn: () => listAllFormFieldGroups(versionId),
  });
  
  // Handle navigation from validation errors - store the slug to open after data loads
  useEffect(() => {
    if (openFieldSlug) {
      setPendingOpenSlug(openFieldSlug);
      onOpenFieldSlugConsumed?.();
    }
  }, [openFieldSlug, onOpenFieldSlugConsumed]);
  
  // Group dialog states
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<FormFieldGroupWithFields | null>(null);
  const [groupForm, setGroupForm] = useState({
    name: "",
    slug: "",
    description: "",
    order: 0,
    parent_id: null as number | null,
    display_context: "form" as "form" | "save",
    visibility_conditions: null as VisibilityConditions | null,
    validation_rules: null as GroupValidationRules | null,
    label_style: null as LabelStyle | null,
  });
  
  // Field dialog states
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [isEditingField, setIsEditingField] = useState(false);
  const [selectedField, setSelectedField] = useState<FormFieldType | null>(null);
  const [createNewGroupInFieldDialog, setCreateNewGroupInFieldDialog] = useState(false);
  const [newGroupInFieldDialog, setNewGroupInFieldDialog] = useState({
    name: "",
    slug: "",
    description: "",
    parent_id: null as number | null,
  });

  const [fieldForm, setFieldForm] = useState({
    name: "",
    slug: "",
    label: "",
    description: "",
    implementation_note: "",
    placeholder: "",
    tooltip: "",
    field_type: "number" as FieldType,
    group_id: 0,
    order: 0,
    required: false,
    min_value: undefined as number | undefined,
    max_value: undefined as number | undefined,
    integer_only: false,
    recommended_min_value: undefined as number | undefined,
    recommended_max_value: undefined as number | undefined,
    recommended_warning_message: undefined as string | undefined,
    min_length: undefined as number | undefined,
    max_length: undefined as number | undefined,
    enum_values: [] as string[],
    reference_table_id: undefined as number | undefined,
    reference_column: "",
    reference_value_column: "",
    reference_filter: null as ReferenceFilter | null,
    default_value: "",
    visibility_conditions: null as VisibilityConditions | null,
    conditional_constraints: null as ConditionalConstraints | null,
    conditional_required: null as ConditionalRequired | null,
    width: null as string | null,
    excel_source: "",
    label_style: null as LabelStyle | null,
  });

  const [newEnumValue, setNewEnumValue] = useState("");
  const [slugUsageInfo, setSlugUsageInfo] = useState<SlugUsageResponse | null>(null);
  const [isLoadingSlugUsage, setIsLoadingSlugUsage] = useState(false);

  // Import template dialog states
  const [importTemplateDialogOpen, setImportTemplateDialogOpen] = useState(false);
  const [selectedTemplateGroup, setSelectedTemplateGroup] = useState<FormFieldGroupTemplateWithFields | null>(null);
  const [selectedTemplateField, setSelectedTemplateField] = useState<FormFieldTemplate | null>(null);
  const [targetGroupIdForFieldImport, setTargetGroupIdForFieldImport] = useState<number | null>(null);

  const { data: references } = useQuery({
    queryKey: ["references", "all"],
    queryFn: () => listAllReferenceTables(),
  });

  // Query for templates
  const { data: groupTemplates } = useQuery({
    queryKey: ["group-templates-with-fields"],
    queryFn: listAllGroupTemplatesWithFields,
    enabled: importTemplateDialogOpen,
  });

  const { data: fieldTemplates } = useQuery({
    queryKey: ["field-templates-standalone"],
    queryFn: () => listFieldTemplates({ standalone_only: true, page_size: 100 }),
    enabled: importTemplateDialogOpen,
  });

  // Filter groups and fields recursively based on search query
  const filteredGroups = useMemo(() => {
    if (!groups || !searchQuery.trim()) return groups;
    
    const query = searchQuery.toLowerCase().trim();
    
    const filterGroupRecursively = (group: FormFieldGroupWithFields): FormFieldGroupWithFields | null => {
      // Filter fields that match the search query
      const matchingFields = group.fields.filter(
        (field) =>
          field.slug.toLowerCase().includes(query) ||
          field.name.toLowerCase().includes(query) ||
          field.label.toLowerCase().includes(query)
      );
      
      // Recursively filter children
      const filteredChildren = group.children
        .map(filterGroupRecursively)
        .filter((child): child is FormFieldGroupWithFields => child !== null);
      
      // Check if group matches
      const groupMatches = group.slug.toLowerCase().includes(query) || group.name.toLowerCase().includes(query);
      
      // Include group if it matches, has matching fields, or has matching children
      if (groupMatches || matchingFields.length > 0 || filteredChildren.length > 0) {
        return {
          ...group,
          // If group matches, show all fields; otherwise show only matching ones
          fields: groupMatches ? group.fields : matchingFields,
          children: filteredChildren,
        };
      }
      return null;
    };
    
    return groups
      .map(filterGroupRecursively)
      .filter((group): group is FormFieldGroupWithFields => group !== null);
  }, [groups, searchQuery]);

  // Get all fields from groups recursively (for conditions)
  const getAllFieldsRecursive = (groupList: FormFieldGroupWithFields[]): FormFieldType[] => {
    const fields: FormFieldType[] = [];
    for (const group of groupList) {
      fields.push(...group.fields);
      if (group.children && group.children.length > 0) {
        fields.push(...getAllFieldsRecursive(group.children));
      }
    }
    return fields;
  };
  const allFields = groups ? getAllFieldsRecursive(groups) : [];
  const selectedGroupFields = useMemo(
    () => (selectedGroup ? getAllFieldsRecursive([selectedGroup]) : []),
    [selectedGroup]
  );

  const getConditionValueOptions = (field: FormFieldType | undefined): string[] => {
    if (!field) return [];
    const isSelect = field.field_type === "select" || field.field_type === "multiselect" || field.field_type === "radio";
    if (!isSelect) return [];

    const seen = new Set<string>();
    const options: string[] = [];

    const addValue = (v: string) => {
      if (v && !seen.has(v)) {
        seen.add(v);
        options.push(v);
      }
    };

    const addFromTable = (tableId: number, displayCol: string, filter?: ReferenceFilter | null) => {
      if (!references?.items) return;
      const table = references.items.find((t) => t.id === tableId);
      if (!table?.data) return;
      let data = table.data;
      if (filter?.column && filter.values.length > 0) {
        data = data.filter((row) => {
          const cellValue = String(row[filter.column] ?? "");
          return filter.values.includes(cellValue);
        });
      }
      for (const row of data) {
        const val = row[displayCol];
        if (val !== null && val !== undefined && val !== "") {
          addValue(String(val));
        }
      }
    };

    if (field.reference_table_id && field.reference_column) {
      addFromTable(field.reference_table_id, field.reference_column, field.reference_filter);
    }

    if (field.conditional_constraints?.rules) {
      for (const rule of field.conditional_constraints.rules) {
        if (rule.reference_table_id && rule.reference_column) {
          addFromTable(rule.reference_table_id, rule.reference_column);
        }
        if (rule.allowed_values) {
          for (const v of rule.allowed_values) addValue(v);
        }
      }
    }

    if (field.enum_values) {
      for (const v of field.enum_values) addValue(v);
    }

    return options;
  };

  // Get all fields with their full path (group_slug.field_slug notation)
  interface FieldWithPath {
    field: FormFieldType;
    fullPath: string;
    label: string;
  }
  
  const getFieldsWithFullPath = (groupList: FormFieldGroupWithFields[]): FieldWithPath[] => {
    const result: FieldWithPath[] = [];
    
    const traverse = (groups: FormFieldGroupWithFields[], pathParts: string[]) => {
      for (const group of groups) {
        const currentPath = [...pathParts, group.slug];
        for (const field of group.fields) {
          result.push({
            field,
            fullPath: [...currentPath, field.slug].join('.'),
            label: field.label,
          });
        }
        traverse(group.children, currentPath);
      }
    };
    
    traverse(groupList, []);
    return result;
  };
  
  const fieldsWithFullPath = groups ? getFieldsWithFullPath(groups) : [];

  // Helper function to find a field by slug recursively
  const findFieldBySlug = (groupList: FormFieldGroupWithFields[], slug: string): FormFieldType | null => {
    for (const group of groupList) {
      const field = group.fields.find(f => f.slug === slug);
      if (field) return field;
      const childField = findFieldBySlug(group.children, slug);
      if (childField) return childField;
    }
    return null;
  };

  // Open field dialog when data is loaded and there's a pending slug
  useEffect(() => {
    if (pendingOpenSlug && groups && !isLoading) {
      const fieldToOpen = findFieldBySlug(groups, pendingOpenSlug);
      if (fieldToOpen) {
        // Use setTimeout to ensure state is ready
        setTimeout(() => {
          handleEditField(fieldToOpen);
        }, 100);
      }
      setPendingOpenSlug(null);
    }
  }, [pendingOpenSlug, groups, isLoading]);

  const invalidateGroups = () => {
    queryClient.invalidateQueries({ queryKey: ["form-field-groups", versionId] });
  };

  // Flatten all groups for selection
  const flattenGroups = (groups: FormFieldGroupWithFields[]): { group: FormFieldGroupWithFields; depth: number }[] => {
    const result: { group: FormFieldGroupWithFields; depth: number }[] = [];
    const flatten = (groups: FormFieldGroupWithFields[], depth: number) => {
      for (const group of groups) {
        result.push({ group, depth });
        flatten(group.children, depth + 1);
      }
    };
    flatten(groups, 0);
    return result;
  };

  const flatGroups = groups ? flattenGroups(groups) : [];

  // Group mutations
  const createGroupMutation = useMutation({
    mutationFn: (data: typeof groupForm) => createFormFieldGroup(versionId, data),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe créé");
      closeGroupDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<typeof groupForm> }) =>
      updateFormFieldGroup(versionId, id, data),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe mis à jour");
      closeGroupDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id: number) => deleteFormFieldGroup(versionId, id),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe supprimé");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Field mutations
  const createFieldMutation = useMutation({
    mutationFn: async (data: typeof fieldForm) => {
      let groupId = data.group_id;
      
      // If creating a new group, do it first
      if (createNewGroupInFieldDialog && newGroupInFieldDialog.name) {
        const newGroup = await createFormFieldGroup(versionId, {
          ...newGroupInFieldDialog,
          slug: newGroupInFieldDialog.slug || slugify(newGroupInFieldDialog.name),
          order: 0,
        });
        groupId = newGroup.id;
      }
      
      return createFormField(versionId, {
        ...data,
        group_id: groupId,
        enum_values: data.enum_values.length > 0 ? data.enum_values : undefined,
        default_value: data.default_value || undefined,
        visibility_conditions: data.visibility_conditions,
        conditional_constraints: data.conditional_constraints,
        conditional_required: data.conditional_required,
        width: (data.width || undefined) as FieldWidth | undefined,
        label_style: data.label_style || undefined,
        // Clear reference columns if no reference table is selected
        reference_column: data.reference_table_id ? data.reference_column : "",
        reference_value_column: data.reference_table_id ? data.reference_value_column : "",
        reference_filter: data.reference_table_id ? data.reference_filter : null,
      });
    },
    onSuccess: () => {
      invalidateGroups();
      toast.success("Champ créé");
      closeFieldDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateFieldMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<typeof fieldForm> }) => {
      // Convert undefined to null for nullable fields so they are properly sent to API
      // Only convert if the key exists in data (to avoid sending null for unrelated partial updates)
      const processedData: Record<string, unknown> = { ...data };
      const nullableFields = [
        'min_value', 'max_value', 
        'recommended_min_value', 'recommended_max_value', 'recommended_warning_message',
        'min_length', 'max_length'
      ];
      for (const field of nullableFields) {
        if (field in data && data[field as keyof typeof data] === undefined) {
          processedData[field] = null;
        }
      }
      
      return updateFormField(versionId, id, {
        ...processedData,
        enum_values: data.enum_values && data.enum_values.length > 0 ? data.enum_values : undefined,
        visibility_conditions: data.visibility_conditions,
        conditional_constraints: data.conditional_constraints,
        conditional_required: data.conditional_required,
        width: (data.width || null) as FieldWidth | null,
        label_style: data.label_style || null,
        // Clear reference columns if no reference table is selected
        reference_column: data.reference_table_id ? data.reference_column : "",
        reference_value_column: data.reference_table_id ? data.reference_value_column : "",
        reference_filter: data.reference_table_id ? data.reference_filter : null,
      });
    },
    onSuccess: () => {
      invalidateGroups();
      toast.success("Champ mis à jour");
      closeFieldDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteFieldMutation = useMutation({
    mutationFn: (id: number) => deleteFormField(versionId, id),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Champ supprimé");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Import template mutations
  const importGroupTemplateMutation = useMutation({
    mutationFn: ({ templateId, targetGroupId }: { templateId: number; targetGroupId?: number }) =>
      applyGroupTemplateToVersion(templateId, versionId, targetGroupId),
    onSuccess: (data) => {
      invalidateGroups();
      toast.success(`Modèle importé : ${data.fields_created} champ(s) créé(s)`);
      setImportTemplateDialogOpen(false);
      setSelectedTemplateGroup(null);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const importFieldTemplateMutation = useMutation({
    mutationFn: ({ templateId, targetGroupId }: { templateId: number; targetGroupId: number }) =>
      applyFieldTemplateToVersion(templateId, versionId, targetGroupId),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Champ importé depuis le modèle");
      setImportTemplateDialogOpen(false);
      setSelectedTemplateField(null);
      setTargetGroupIdForFieldImport(null);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Reorder mutations
  const reorderGroupsMutation = useMutation({
    mutationFn: (groupOrders: { id: number; order: number }[]) =>
      reorderFormFieldGroups(versionId, groupOrders),
    onSuccess: () => {
      invalidateGroups();
    },
    onError: (error) => toast.error("Erreur lors du réordonnancement", { description: getErrorMessage(error) }),
  });

  const reorderFieldsMutation = useMutation({
    mutationFn: (fieldOrders: { id: number; order: number }[]) =>
      reorderFormFields(versionId, fieldOrders),
    onSuccess: () => {
      invalidateGroups();
    },
    onError: (error) => toast.error("Erreur lors du réordonnancement", { description: getErrorMessage(error) }),
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handlers
  const toggleGroup = (groupId: number) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setIsEditingGroup(false);
    setSelectedGroup(null);
    setGroupForm({ name: "", slug: "", description: "", order: 0, parent_id: null, display_context: "form", visibility_conditions: null, validation_rules: null, label_style: null });
  };

  const closeFieldDialog = () => {
    setFieldDialogOpen(false);
    setIsEditingField(false);
    setSelectedField(null);
    setCreateNewGroupInFieldDialog(false);
    setNewGroupInFieldDialog({ name: "", slug: "", description: "", parent_id: null });
    setNewEnumValue("");
    setSlugUsageInfo(null);
    setFieldForm({
      name: "",
      slug: "",
      label: "",
      description: "",
      implementation_note: "",
      placeholder: "",
      tooltip: "",
      field_type: "number",
      group_id: 0,
      order: 0,
      required: false,
      min_value: undefined,
      max_value: undefined,
      integer_only: false,
      recommended_min_value: undefined,
      recommended_max_value: undefined,
      recommended_warning_message: undefined,
      min_length: undefined,
      max_length: undefined,
      enum_values: [],
      reference_table_id: undefined,
      reference_column: "",
      reference_value_column: "",
      reference_filter: null,
      default_value: "",
      visibility_conditions: null,
      conditional_constraints: null,
      conditional_required: null,
      width: null,
      excel_source: "",
      label_style: null,
    });
  };

  const handleEditGroup = (group: FormFieldGroupWithFields) => {
    setSelectedGroup(group);
    setGroupForm({
      name: group.name,
      slug: group.slug,
      description: group.description || "",
      order: group.order,
      parent_id: group.parent_id,
      display_context: group.display_context || "form",
      visibility_conditions: group.visibility_conditions,
      validation_rules: group.validation_rules,
      label_style: group.label_style ?? null,
    });
    setIsEditingGroup(true);
    setGroupDialogOpen(true);
  };

  const handleAddSubGroup = (parentGroup: FormFieldGroupWithFields) => {
    setGroupForm({
      name: "",
      slug: "",
      description: "",
      order: 0,
      parent_id: parentGroup.id,
      display_context: "form",
      visibility_conditions: null,
      validation_rules: null,
      label_style: null,
    });
    setIsEditingGroup(false);
    setGroupDialogOpen(true);
  };

  const handleAddField = (groupId: number) => {
    // Find the group to calculate the next order
    const findGroup = (groups: FormFieldGroupWithFields[]): FormFieldGroupWithFields | undefined => {
      for (const group of groups) {
        if (group.id === groupId) return group;
        const found = findGroup(group.children);
        if (found) return found;
      }
      return undefined;
    };
    
    const targetGroup = groups ? findGroup(groups) : undefined;
    const maxOrder = targetGroup?.fields?.length 
      ? Math.max(...targetGroup.fields.map(f => f.order)) + 1 
      : 0;
    
    setFieldForm({
      ...fieldForm,
      group_id: groupId,
      order: maxOrder,
    });
    setFieldDialogOpen(true);
  };

  const handleEditField = async (field: FormFieldType) => {
    setSelectedField(field);
    setNewEnumValue("");
    setFieldForm({
      name: field.name,
      slug: field.slug,
      label: field.label,
      description: field.description || "",
      implementation_note: field.implementation_note || "",
      placeholder: field.placeholder || "",
      tooltip: field.tooltip || "",
      field_type: field.field_type,
      group_id: field.group_id,
      order: field.order,
      required: field.required,
      // Use ?? instead of || to preserve 0 values (0 is falsy but valid)
      min_value: field.min_value ?? undefined,
      max_value: field.max_value ?? undefined,
      integer_only: field.integer_only ?? false,
      recommended_min_value: field.recommended_min_value ?? undefined,
      recommended_max_value: field.recommended_max_value ?? undefined,
      recommended_warning_message: field.recommended_warning_message ?? undefined,
      min_length: field.min_length ?? undefined,
      max_length: field.max_length ?? undefined,
      enum_values: field.enum_values || [],
      reference_table_id: field.reference_table_id ?? undefined,
      reference_column: field.reference_column || "",
      reference_value_column: field.reference_value_column || "",
      reference_filter: field.reference_filter ?? null,
      default_value: String(field.default_value ?? ""),
      visibility_conditions: field.visibility_conditions,
      conditional_constraints: field.conditional_constraints,
      conditional_required: field.conditional_required ?? null,
      width: field.width ?? null,
      excel_source: field.excel_source || "",
      label_style: field.label_style ?? null,
    });
    setIsEditingField(true);
    setFieldDialogOpen(true);
    
    // Check if slug is used elsewhere
    setIsLoadingSlugUsage(true);
    try {
      const usageInfo = await checkFormFieldSlugUsage(versionId, field.id);
      setSlugUsageInfo(usageInfo);
    } catch {
      // If check fails, assume slug is used (safer)
      setSlugUsageInfo({ slug: field.slug, is_used: true, usages: [], can_edit_slug: false });
    } finally {
      setIsLoadingSlugUsage(false);
    }
  };

  const handleDuplicateField = (field: FormFieldType) => {
    // Find the group to calculate the next order
    const findGroup = (groups: FormFieldGroupWithFields[]): FormFieldGroupWithFields | undefined => {
      for (const group of groups) {
        if (group.id === field.group_id) return group;
        const found = findGroup(group.children);
        if (found) return found;
      }
      return undefined;
    };
    
    const group = groups ? findGroup(groups) : undefined;
    const maxOrder = group 
      ? Math.max(...group.fields.map(f => f.order), -1) + 1
      : 0;

    // Reset slug usage info for new field
    setSlugUsageInfo(null);
    setIsLoadingSlugUsage(false);
    setSelectedField(null);
    setNewEnumValue("");
    
    setFieldForm({
      name: field.name + "_copie",
      slug: field.slug + "_copie",
      label: field.label + " (copie)",
      description: field.description || "",
      implementation_note: field.implementation_note || "",
      placeholder: field.placeholder || "",
      tooltip: field.tooltip || "",
      field_type: field.field_type,
      group_id: field.group_id,
      order: maxOrder,
      required: field.required,
      // Use ?? instead of || to preserve 0 values (0 is falsy but valid)
      min_value: field.min_value ?? undefined,
      max_value: field.max_value ?? undefined,
      integer_only: field.integer_only ?? false,
      recommended_min_value: field.recommended_min_value ?? undefined,
      recommended_max_value: field.recommended_max_value ?? undefined,
      recommended_warning_message: field.recommended_warning_message ?? undefined,
      min_length: field.min_length ?? undefined,
      max_length: field.max_length ?? undefined,
      enum_values: field.enum_values || [],
      reference_table_id: field.reference_table_id ?? undefined,
      reference_column: field.reference_column || "",
      reference_value_column: field.reference_value_column || "",
      reference_filter: field.reference_filter ?? null,
      default_value: String(field.default_value ?? ""),
      visibility_conditions: field.visibility_conditions,
      conditional_constraints: field.conditional_constraints,
      conditional_required: field.conditional_required ?? null,
      width: field.width ?? null,
      excel_source: field.excel_source || "",
      label_style: field.label_style ?? null,
    });
    setIsEditingField(false);
    setFieldDialogOpen(true);
  };

  const handleMoveField = (fieldId: number, newGroupId: number) => {
    updateFieldMutation.mutate({
      id: fieldId,
      data: { group_id: newGroupId },
    });
  };

  const handleFieldNameChange = (name: string) => {
    setFieldForm({
      ...fieldForm,
      name,
      slug: isEditingField ? fieldForm.slug : slugify(name),
      label: isEditingField ? fieldForm.label : name,
    });
  };

  const getTypeBadge = (type: FieldType) => {
    const colors: Record<FieldType, string> = {
      text: "bg-info/20 text-info",
      number: "bg-success/20 text-success",
      select: "bg-laposte-navy/20 text-laposte-navy",
      multiselect: "bg-laposte-navy/20 text-laposte-navy",
      checkbox: "bg-warning/20 text-warning",
      date: "bg-laposte-magenta/20 text-laposte-magenta",
      textarea: "bg-laposte-teal/20 text-laposte-teal",
      radio: "bg-laposte-blue/20 text-laposte-blue",
    };
    return <Badge className={colors[type]}>{fieldTypeLabels[type]}</Badge>;
  };


  // Handle group drag end
  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !groups) return;

    const activeId = Number(String(active.id).replace("group-", ""));
    const overId = Number(String(over.id).replace("group-", ""));

    const oldIndex = groups.findIndex((g) => g.id === activeId);
    const newIndex = groups.findIndex((g) => g.id === overId);

    if (oldIndex === -1 || newIndex === -1) return;

    const newGroups = arrayMove(groups, oldIndex, newIndex);
    const groupOrders = newGroups.map((g, index) => ({
      id: g.id,
      order: index,
    }));

    reorderGroupsMutation.mutate(groupOrders);
  };

  // Handle field drag end within a group
  const handleFieldDragEnd = (groupId: number, activeId: string, overId: string) => {
    if (!groups) return;

    const findGroupWithField = (groups: FormFieldGroupWithFields[], fieldId: number): FormFieldGroupWithFields | undefined => {
      for (const group of groups) {
        if (group.id === groupId) return group;
        const found = findGroupWithField(group.children, fieldId);
        if (found) return found;
      }
      return undefined;
    };

    const group = findGroupWithField(groups, groupId);
    if (!group) return;

    const activeFieldId = Number(activeId.replace("field-", ""));
    const overFieldId = Number(overId.replace("field-", ""));

    const sortedFields = [...group.fields].sort((a, b) => a.order - b.order);
    const oldIndex = sortedFields.findIndex((f) => f.id === activeFieldId);
    const newIndex = sortedFields.findIndex((f) => f.id === overFieldId);

    if (oldIndex === -1 || newIndex === -1) return;

    const newFields = arrayMove(sortedFields, oldIndex, newIndex);
    const fieldOrders = newFields.map((f, index) => ({
      id: f.id,
      order: index,
    }));

    reorderFieldsMutation.mutate(fieldOrders);
  };

  // Handle child group drag end within a parent group
  const handleChildGroupDragEnd = (parentId: number, activeId: string, overId: string) => {
    if (!groups) return;

    const findGroupById = (groups: FormFieldGroupWithFields[], targetId: number): FormFieldGroupWithFields | undefined => {
      for (const group of groups) {
        if (group.id === targetId) return group;
        const found = findGroupById(group.children, targetId);
        if (found) return found;
      }
      return undefined;
    };

    const parentGroup = findGroupById(groups, parentId);
    if (!parentGroup) return;

    const activeGroupId = Number(activeId.replace("group-", ""));
    const overGroupId = Number(overId.replace("group-", ""));

    const sortedChildren = [...parentGroup.children].sort((a, b) => a.order - b.order);
    const oldIndex = sortedChildren.findIndex((g) => g.id === activeGroupId);
    const newIndex = sortedChildren.findIndex((g) => g.id === overGroupId);

    if (oldIndex === -1 || newIndex === -1) return;

    const newChildren = arrayMove(sortedChildren, oldIndex, newIndex);
    const groupOrders = newChildren.map((g, index) => ({
      id: g.id,
      order: index,
    }));

    reorderGroupsMutation.mutate(groupOrders);
  };

  // Render child group with proper depth
  const renderChildGroup = (group: FormFieldGroupWithFields, depth: number): React.ReactNode => (
    <SortableGroup
      key={group.id}
      group={group}
      depth={depth}
      isEditable={isEditable}
      isExpanded={expandedGroups.has(group.id)}
      onToggle={() => toggleGroup(group.id)}
      onEditGroup={handleEditGroup}
      onDeleteGroup={(id) => deleteGroupMutation.mutate(id)}
      onAddSubGroup={handleAddSubGroup}
      onAddField={handleAddField}
      onEditField={handleEditField}
      onDuplicateField={handleDuplicateField}
      onDeleteField={(id) => deleteFieldMutation.mutate(id)}
      onMoveField={handleMoveField}
      getTypeBadge={getTypeBadge}
      onFieldDragEnd={handleFieldDragEnd}
      onChildGroupDragEnd={handleChildGroupDragEnd}
      renderChildGroup={renderChildGroup}
      expandedGroups={expandedGroups}
      onToggleGroup={toggleGroup}
      flatGroups={flatGroups}
    />
  );

  // Sorted groups for DnD (use filtered groups when searching)
  const sortedGroups = useMemo(
    () => filteredGroups ? [...filteredGroups].sort((a, b) => a.order - b.order) : [],
    [filteredGroups]
  );
  const groupIds = useMemo(
    () => sortedGroups.map((g) => `group-${g.id}`),
    [sortedGroups]
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasNoGroups = !groups || groups.length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Champs de formulaire</CardTitle>
          <CardDescription>
            Configurez les champs de saisie du simulateur organisés par groupes
          </CardDescription>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-[220px]"
            />
          </div>
          {isEditable && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setImportTemplateDialogOpen(true)}>
                <LayoutTemplate className="mr-2 h-4 w-4" />
                Importer un modèle
              </Button>
              <Button variant="outline" onClick={() => setGroupDialogOpen(true)}>
                <FolderPlus className="mr-2 h-4 w-4" />
                Nouveau groupe
              </Button>
              {!hasNoGroups && (
                <Button onClick={() => setFieldDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Nouveau champ
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {hasNoGroups ? (
          <div className="text-center py-12 text-muted-foreground">
            <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="mb-2">Aucun groupe de champs</p>
            <p className="text-sm mb-4">
              Créez d&apos;abord un groupe pour pouvoir ajouter des champs
            </p>
            {isEditable && (
              <Button
                variant="outline"
                onClick={() => setGroupDialogOpen(true)}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                Créer un groupe
              </Button>
            )}
          </div>
        ) : sortedGroups.length === 0 && searchQuery ? (
          <div className="text-center py-12 text-muted-foreground">
            <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucun résultat pour &quot;{searchQuery}&quot;</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setSearchQuery("")}
            >
              Effacer la recherche
            </Button>
          </div>
        ) : isEditable ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleGroupDragEnd}
          >
            <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {sortedGroups.map((group) => (
                  <SortableGroup
                    key={group.id}
                    group={group}
                    depth={0}
                    isEditable={isEditable}
                    isExpanded={expandedGroups.has(group.id)}
                    onToggle={() => toggleGroup(group.id)}
                    onEditGroup={handleEditGroup}
                    onDeleteGroup={(id) => deleteGroupMutation.mutate(id)}
                    onAddSubGroup={handleAddSubGroup}
                    onAddField={handleAddField}
                    onEditField={handleEditField}
                    onDuplicateField={handleDuplicateField}
                    onDeleteField={(id) => deleteFieldMutation.mutate(id)}
                    onMoveField={handleMoveField}
                    getTypeBadge={getTypeBadge}
                    onFieldDragEnd={handleFieldDragEnd}
                    onChildGroupDragEnd={handleChildGroupDragEnd}
                    renderChildGroup={renderChildGroup}
                    expandedGroups={expandedGroups}
                    onToggleGroup={toggleGroup}
                    flatGroups={flatGroups}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="space-y-3">
            {sortedGroups.map((group) => (
              <SortableGroup
                key={group.id}
                group={group}
                depth={0}
                isEditable={isEditable}
                isExpanded={expandedGroups.has(group.id)}
                onToggle={() => toggleGroup(group.id)}
                onEditGroup={handleEditGroup}
                onDeleteGroup={(id) => deleteGroupMutation.mutate(id)}
                onAddSubGroup={handleAddSubGroup}
                onAddField={handleAddField}
                onEditField={handleEditField}
                onDuplicateField={handleDuplicateField}
                onDeleteField={(id) => deleteFieldMutation.mutate(id)}
                onMoveField={handleMoveField}
                getTypeBadge={getTypeBadge}
                onFieldDragEnd={handleFieldDragEnd}
                onChildGroupDragEnd={handleChildGroupDragEnd}
                renderChildGroup={renderChildGroup}
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                flatGroups={flatGroups}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={closeGroupDialog}>
        <DialogContent className="max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {isEditingGroup ? "Modifier le groupe" : "Nouveau groupe"}
            </DialogTitle>
            <DialogDescription>
              {groupForm.parent_id
                ? "Ce groupe sera créé comme sous-groupe"
                : "Créez un groupe pour organiser vos champs de formulaire"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="group-name">Nom</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-xs mb-1 font-medium">Mise en forme disponible :</p>
                    <ul className="text-xs space-y-0.5">
                      <li><code className="bg-muted px-1 rounded">[b]texte[/b]</code> - gras</li>
                      <li><code className="bg-muted px-1 rounded">[i]texte[/i]</code> - italique</li>
                      <li><code className="bg-muted px-1 rounded">[u]texte[/u]</code> - souligné</li>
                      <li><code className="bg-muted px-1 rounded">[s]texte[/s]</code> - barré</li>
                      <li><code className="bg-muted px-1 rounded">[color=#hex]texte[/color]</code> - couleur</li>
                      <li><code className="bg-muted px-1 rounded">[bg=#hex]texte[/bg]</code> - surlignage</li>
                    </ul>
                    <p className="text-xs mt-1 text-muted-foreground">Ex: [b]Sans[/b] option de livraison</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                id="group-name"
                value={groupForm.name}
                onChange={(e) =>
                  setGroupForm({
                    ...groupForm,
                    name: e.target.value,
                    slug: isEditingGroup ? groupForm.slug : slugify(e.target.value),
                  })
                }
                placeholder="Ex: Informations personnelles"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-slug">Slug</Label>
              <Input
                id="group-slug"
                value={groupForm.slug}
                onChange={(e) => setGroupForm({ ...groupForm, slug: e.target.value })}
              />
              {isEditingGroup && (
                <p className="text-xs text-muted-foreground">
                  Attention : modifier le slug peut impacter les références existantes
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-parent">Groupe parent</Label>
              <Select
                value={groupForm.parent_id?.toString() || "none"}
                onValueChange={(value) =>
                  setGroupForm({
                    ...groupForm,
                    parent_id: value !== "none" ? parseInt(value) : null,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Aucun (groupe racine)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun (groupe racine)</SelectItem>
                  {flatGroups
                    .filter(({ group }) => group.id !== selectedGroup?.id)
                    .map(({ group, depth }) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {"─".repeat(depth)} {group.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-description">Description</Label>
              <Textarea
                id="group-description"
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="group-display-context">Contexte d'affichage</Label>
              <Select
                value={groupForm.display_context}
                onValueChange={(value: "form" | "save") =>
                  setGroupForm({ ...groupForm, display_context: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="form">Formulaire de simulation</SelectItem>
                  <SelectItem value="save">Dialog d'enregistrement</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {groupForm.display_context === "save"
                  ? "Ce groupe sera affiché dans le dialog d'enregistrement de la cotation, pas dans le formulaire de simulation."
                  : "Ce groupe sera affiché dans le formulaire de simulation."}
              </p>
            </div>

            {/* Visibility Conditions Section for Groups */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  <Label>Conditions de visibilité</Label>
                </div>
                {!groupForm.visibility_conditions ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setGroupForm({
                        ...groupForm,
                        visibility_conditions: {
                          logic: "AND",
                          conditions: [{ field_slug: "", operator: "equals", value: "" }],
                        },
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter des conditions
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setGroupForm({ ...groupForm, visibility_conditions: null })}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                )}
              </div>

              {groupForm.visibility_conditions && (
                <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Afficher ce groupe si</span>
                    <Select
                      value={groupForm.visibility_conditions.logic}
                      onValueChange={(value: ConditionLogic) =>
                        setGroupForm({
                          ...groupForm,
                          visibility_conditions: {
                            ...groupForm.visibility_conditions!,
                            logic: value,
                          },
                        })
                      }
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">TOUTES</SelectItem>
                        <SelectItem value="OR">AU MOINS UNE</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">des conditions sont remplies :</span>
                  </div>

                  <div className="space-y-2">
                    {groupForm.visibility_conditions.conditions.map((condition, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <FieldSlugSelector
                          value={condition.field_slug}
                          onSelect={(slug) => {
                            const newConditions = [...groupForm.visibility_conditions!.conditions];
                            newConditions[index] = { ...newConditions[index], field_slug: slug };
                            setGroupForm({
                              ...groupForm,
                              visibility_conditions: {
                                ...groupForm.visibility_conditions!,
                                conditions: newConditions,
                              },
                            });
                          }}
                          fields={fieldsWithFullPath}
                          placeholder="Champ..."
                          className="flex-1"
                        />

                        <Select
                          value={condition.operator}
                          onValueChange={(value: ConditionOperator) => {
                            const newConditions = [...groupForm.visibility_conditions!.conditions];
                            newConditions[index] = { ...newConditions[index], operator: value };
                            setGroupForm({
                              ...groupForm,
                              visibility_conditions: {
                                ...groupForm.visibility_conditions!,
                                conditions: newConditions,
                              },
                            });
                          }}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(operatorLabels).map(([op, label]) => (
                              <SelectItem key={op} value={op}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {!["is_empty", "is_not_empty"].includes(condition.operator) && (
                          (() => {
                            const selectedField = allFields.find(f => f.slug === condition.field_slug);
                            const isCheckbox = selectedField?.field_type === "checkbox";
                            const valueOptions = getConditionValueOptions(selectedField);
                            const hasOptions = valueOptions.length > 0;
                            const showSelectForValue = ["equals", "not_equals", "in", "not_in"].includes(condition.operator);
                            
                            if (isCheckbox) {
                              return (
                                <Select
                                  value={String(condition.value ?? "")}
                                  onValueChange={(value) => {
                                    const newConditions = [...groupForm.visibility_conditions!.conditions];
                                    newConditions[index] = { ...newConditions[index], value };
                                    setGroupForm({
                                      ...groupForm,
                                      visibility_conditions: {
                                        ...groupForm.visibility_conditions!,
                                        conditions: newConditions,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="État..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="true">Coché</SelectItem>
                                    <SelectItem value="false">Décoché</SelectItem>
                                  </SelectContent>
                                </Select>
                              );
                            }
                            
                            if (hasOptions && showSelectForValue) {
                              return (
                                <Select
                                  value={String(condition.value ?? "")}
                                  onValueChange={(value) => {
                                    const newConditions = [...groupForm.visibility_conditions!.conditions];
                                    newConditions[index] = { ...newConditions[index], value };
                                    setGroupForm({
                                      ...groupForm,
                                      visibility_conditions: {
                                        ...groupForm.visibility_conditions!,
                                        conditions: newConditions,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Sélectionner une valeur..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {valueOptions.map((opt) => (
                                      <SelectItem key={opt} value={opt}>
                                        {opt}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              );
                            }
                            
                            return (
                              <Input
                                className="flex-1"
                                placeholder="Valeur..."
                                value={String(condition.value ?? "")}
                                onChange={(e) => {
                                  const newConditions = [...groupForm.visibility_conditions!.conditions];
                                  newConditions[index] = { ...newConditions[index], value: e.target.value };
                                  setGroupForm({
                                    ...groupForm,
                                    visibility_conditions: {
                                      ...groupForm.visibility_conditions!,
                                      conditions: newConditions,
                                    },
                                  });
                                }}
                              />
                            );
                          })()
                        )}

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newConditions = groupForm.visibility_conditions!.conditions.filter(
                              (_, i) => i !== index
                            );
                            if (newConditions.length === 0) {
                              setGroupForm({ ...groupForm, visibility_conditions: null });
                            } else {
                              setGroupForm({
                                ...groupForm,
                                visibility_conditions: {
                                  ...groupForm.visibility_conditions!,
                                  conditions: newConditions,
                                },
                              });
                            }
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setGroupForm({
                        ...groupForm,
                        visibility_conditions: {
                          ...groupForm.visibility_conditions!,
                          conditions: [
                            ...groupForm.visibility_conditions!.conditions,
                            { field_slug: "", operator: "equals", value: "" },
                          ],
                        },
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une condition
                  </Button>
                </div>
              )}
            </div>

            {/* Validation Rules Section */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                  <Label>Règles de validation du groupe</Label>
                </div>
                {!groupForm.validation_rules ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setGroupForm({
                        ...groupForm,
                        validation_rules: {
                          activation: { mode: "auto_any" },
                          when_active: { required_fields: [], at_least_one_of: [] },
                        },
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Configurer
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setGroupForm({ ...groupForm, validation_rules: null })}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                )}
              </div>

              {groupForm.validation_rules && (
                <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                  {/* Activation Mode */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground">Mode d&apos;activation</Label>
                    <Select
                      value={groupForm.validation_rules.activation?.mode || "auto_any"}
                      onValueChange={(value: GroupActivationMode) =>
                        setGroupForm({
                          ...groupForm,
                          validation_rules: {
                            ...groupForm.validation_rules!,
                            activation: {
                              ...groupForm.validation_rules!.activation,
                              mode: value,
                            },
                          },
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto_any">
                          Automatique - au moins un champ rempli
                        </SelectItem>
                        <SelectItem value="auto_key_fields">
                          Automatique - champs clés remplis
                        </SelectItem>
                        <SelectItem value="manual_toggle">
                          Manuel - toggle activé par l&apos;utilisateur
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {groupForm.validation_rules.activation?.mode === "auto_any" && 
                        "Le groupe est actif dès qu'un champ a une valeur"}
                      {groupForm.validation_rules.activation?.mode === "auto_key_fields" && 
                        "Le groupe est actif quand tous les champs clés sont remplis"}
                      {groupForm.validation_rules.activation?.mode === "manual_toggle" && 
                        "L'utilisateur active le groupe via un interrupteur"}
                    </p>
                  </div>

                  {/* Ignore default values option for auto_any mode */}
                  {groupForm.validation_rules.activation?.mode === "auto_any" && (
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="ignore_default_values"
                        checked={groupForm.validation_rules.activation?.ignore_default_values ?? false}
                        onCheckedChange={(checked) =>
                          setGroupForm({
                            ...groupForm,
                            validation_rules: {
                              ...groupForm.validation_rules!,
                              activation: {
                                ...groupForm.validation_rules!.activation!,
                                ignore_default_values: checked,
                              },
                            },
                          })
                        }
                      />
                      <Label htmlFor="ignore_default_values" className="text-sm cursor-pointer">
                        Ignorer les valeurs par défaut
                      </Label>
                    </div>
                  )}

                  {/* Key fields for auto_key_fields mode */}
                  {groupForm.validation_rules.activation?.mode === "auto_key_fields" && (
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground">Champs clés</Label>
                      <Select
                        value="select-field"
                        onValueChange={(value) => {
                          if (value === "select-field") return;
                          const currentKeyFields = groupForm.validation_rules?.activation?.key_fields || [];
                          if (!currentKeyFields.includes(value)) {
                            setGroupForm({
                              ...groupForm,
                              validation_rules: {
                                ...groupForm.validation_rules!,
                                activation: {
                                  ...groupForm.validation_rules!.activation!,
                                  key_fields: [...currentKeyFields, value],
                                },
                              },
                            });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Ajouter un champ clé..." />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedGroupFields
                            .filter(f => !groupForm.validation_rules?.activation?.key_fields?.includes(f.slug))
                            .map((f) => (
                              <SelectItem key={f.slug} value={f.slug}>
                                {f.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <div className="flex flex-wrap gap-2">
                        {groupForm.validation_rules.activation?.key_fields?.map((slug) => {
                          const field = selectedGroupFields.find(f => f.slug === slug);
                          return (
                            <Badge key={slug} variant="secondary" className="gap-1">
                              {field?.label || slug}
                              <button
                                type="button"
                                onClick={() => {
                                  const newKeyFields = groupForm.validation_rules!.activation!.key_fields!.filter(s => s !== slug);
                                  setGroupForm({
                                    ...groupForm,
                                    validation_rules: {
                                      ...groupForm.validation_rules!,
                                      activation: {
                                        ...groupForm.validation_rules!.activation!,
                                        key_fields: newKeyFields,
                                      },
                                    },
                                  });
                                }}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Toggle label for manual_toggle mode */}
                  {groupForm.validation_rules.activation?.mode === "manual_toggle" && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">Label du toggle</Label>
                        <Input
                          value={groupForm.validation_rules.activation?.toggle_label || ""}
                          onChange={(e) =>
                            setGroupForm({
                              ...groupForm,
                              validation_rules: {
                                ...groupForm.validation_rules!,
                                activation: {
                                  ...groupForm.validation_rules!.activation!,
                                  toggle_label: e.target.value,
                                },
                              },
                            })
                          }
                          placeholder="Ex: Activer la tarification colis"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-xs font-medium text-muted-foreground">
                            Afficher les champs quand inactif
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {groupForm.validation_rules.activation?.show_fields_when_inactive !== false
                              ? "Les champs sont affichés grisés"
                              : "Les champs sont masqués"}
                          </p>
                        </div>
                        <Switch
                          checked={groupForm.validation_rules.activation?.show_fields_when_inactive !== false}
                          onCheckedChange={(checked) =>
                            setGroupForm({
                              ...groupForm,
                              validation_rules: {
                                ...groupForm.validation_rules!,
                                activation: {
                                  ...groupForm.validation_rules!.activation!,
                                  show_fields_when_inactive: checked,
                                },
                              },
                            })
                          }
                        />
                      </div>
                    </>
                  )}

                  {/* Required fields when active */}
                  <div className="space-y-2 border-t pt-4">
                    <Label className="text-xs font-medium text-muted-foreground">Champs requis si groupe actif</Label>
                    <Select
                      value="select-field"
                      onValueChange={(value) => {
                        if (value === "select-field") return;
                        const currentRequired = groupForm.validation_rules?.when_active?.required_fields || [];
                        if (!currentRequired.includes(value)) {
                          setGroupForm({
                            ...groupForm,
                            validation_rules: {
                              ...groupForm.validation_rules!,
                              when_active: {
                                ...groupForm.validation_rules!.when_active,
                                required_fields: [...currentRequired, value],
                              },
                            },
                          });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Ajouter un champ requis..." />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedGroupFields
                          .filter(f => !groupForm.validation_rules?.when_active?.required_fields?.includes(f.slug))
                          .map((f) => (
                            <SelectItem key={f.slug} value={f.slug}>
                              {f.label}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2">
                      {groupForm.validation_rules.when_active?.required_fields?.map((slug) => {
                        const field = selectedGroupFields.find(f => f.slug === slug);
                        return (
                          <Badge key={slug} variant="outline" className="gap-1">
                            {field?.label || slug}
                            <button
                              type="button"
                              onClick={() => {
                                const newRequired = groupForm.validation_rules!.when_active!.required_fields!.filter(s => s !== slug);
                                setGroupForm({
                                  ...groupForm,
                                  validation_rules: {
                                    ...groupForm.validation_rules!,
                                    when_active: {
                                      ...groupForm.validation_rules!.when_active,
                                      required_fields: newRequired,
                                    },
                                  },
                                });
                              }}
                              className="ml-1 hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  {/* At least one of rules */}
                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-muted-foreground">Règles &quot;au moins un parmi&quot;</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const currentRules = groupForm.validation_rules?.when_active?.at_least_one_of || [];
                          setGroupForm({
                            ...groupForm,
                            validation_rules: {
                              ...groupForm.validation_rules!,
                              when_active: {
                                ...groupForm.validation_rules!.when_active,
                                at_least_one_of: [...currentRules, { fields: [], message: "" }],
                              },
                            },
                          });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Ajouter une règle
                      </Button>
                    </div>

                    {groupForm.validation_rules.when_active?.at_least_one_of?.map((rule, ruleIndex) => (
                      <div key={ruleIndex} className="p-3 rounded border bg-background space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Règle {ruleIndex + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newRules = groupForm.validation_rules!.when_active!.at_least_one_of!.filter((_, i) => i !== ruleIndex);
                              setGroupForm({
                                ...groupForm,
                                validation_rules: {
                                  ...groupForm.validation_rules!,
                                  when_active: {
                                    ...groupForm.validation_rules!.when_active,
                                    at_least_one_of: newRules,
                                  },
                                },
                              });
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <Select
                          value="select-field"
                          onValueChange={(value) => {
                            if (value === "select-field") return;
                            const newRules = [...groupForm.validation_rules!.when_active!.at_least_one_of!];
                            if (!newRules[ruleIndex].fields.includes(value)) {
                              newRules[ruleIndex] = {
                                ...newRules[ruleIndex],
                                fields: [...newRules[ruleIndex].fields, value],
                              };
                              setGroupForm({
                                ...groupForm,
                                validation_rules: {
                                  ...groupForm.validation_rules!,
                                  when_active: {
                                    ...groupForm.validation_rules!.when_active,
                                    at_least_one_of: newRules,
                                  },
                                },
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Ajouter un champ..." />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedGroupFields
                              .filter(f => !rule.fields.includes(f.slug))
                              .map((f) => (
                                <SelectItem key={f.slug} value={f.slug}>
                                  {f.label}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>

                        <div className="flex flex-wrap gap-2">
                          {rule.fields.map((slug) => {
                            const field = selectedGroupFields.find(f => f.slug === slug);
                            return (
                              <Badge key={slug} variant="secondary" className="gap-1">
                                {field?.label || slug}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newRules = [...groupForm.validation_rules!.when_active!.at_least_one_of!];
                                    newRules[ruleIndex] = {
                                      ...newRules[ruleIndex],
                                      fields: newRules[ruleIndex].fields.filter(s => s !== slug),
                                    };
                                    setGroupForm({
                                      ...groupForm,
                                      validation_rules: {
                                        ...groupForm.validation_rules!,
                                        when_active: {
                                          ...groupForm.validation_rules!.when_active,
                                          at_least_one_of: newRules,
                                        },
                                      },
                                    });
                                  }}
                                  className="ml-1 hover:text-destructive"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            );
                          })}
                        </div>

                        <Input
                          placeholder="Message d'erreur personnalisé"
                          value={rule.message}
                          onChange={(e) => {
                            const newRules = [...groupForm.validation_rules!.when_active!.at_least_one_of!];
                            newRules[ruleIndex] = {
                              ...newRules[ruleIndex],
                              message: e.target.value,
                            };
                            setGroupForm({
                              ...groupForm,
                              validation_rules: {
                                ...groupForm.validation_rules!,
                                when_active: {
                                  ...groupForm.validation_rules!.when_active,
                                  at_least_one_of: newRules,
                                },
                              },
                            });
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Label Style Section */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 7 4 4 20 4 20 7" />
                    <line x1="9" x2="15" y1="20" y2="20" />
                    <line x1="12" x2="12" y1="4" y2="20" />
                  </svg>
                  <Label>Style du libellé</Label>
                </div>
                {!groupForm.label_style ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setGroupForm({
                        ...groupForm,
                        label_style: {},
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Personnaliser
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setGroupForm({ ...groupForm, label_style: null })}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Réinitialiser
                  </Button>
                )}
              </div>

              {groupForm.label_style && (
                <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                  {/* Preview */}
                  <div className="p-3 rounded border bg-background">
                    <p className="text-xs text-muted-foreground mb-1">Aperçu :</p>
                    <span
                      className={cn(
                        "text-sm font-medium text-muted-foreground",
                        groupForm.label_style.fontSize && `text-${groupForm.label_style.fontSize}`,
                        groupForm.label_style.fontWeight && `font-${groupForm.label_style.fontWeight}`,
                        groupForm.label_style.fontStyle === "italic" && "italic",
                        groupForm.label_style.textTransform && groupForm.label_style.textTransform !== "none" && groupForm.label_style.textTransform,
                        groupForm.label_style.color === "primary" && "text-primary",
                        groupForm.label_style.color === "secondary" && "text-secondary-foreground",
                        groupForm.label_style.color === "muted" && "text-muted-foreground",
                        groupForm.label_style.color === "destructive" && "text-destructive",
                        groupForm.label_style.customClassName
                      )}
                      style={groupForm.label_style.color === "custom" && groupForm.label_style.customColor ? { color: groupForm.label_style.customColor } : undefined}
                    >
                      {parseBBCode(groupForm.name || "Nom du groupe")}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Font Size */}
                    <div className="space-y-2">
                      <Label htmlFor="group-label-font-size">Taille</Label>
                      <Select
                        value={groupForm.label_style.fontSize || "default"}
                        onValueChange={(value) =>
                          setGroupForm({
                            ...groupForm,
                            label_style: {
                              ...groupForm.label_style!,
                              fontSize: value === "default" ? undefined : value as LabelFontSize,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="xs">Très petit (xs)</SelectItem>
                          <SelectItem value="sm">Petit (sm)</SelectItem>
                          <SelectItem value="base">Normal (base)</SelectItem>
                          <SelectItem value="lg">Grand (lg)</SelectItem>
                          <SelectItem value="xl">Très grand (xl)</SelectItem>
                          <SelectItem value="2xl">Extra grand (2xl)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Font Weight */}
                    <div className="space-y-2">
                      <Label htmlFor="group-label-font-weight">Poids</Label>
                      <Select
                        value={groupForm.label_style.fontWeight || "default"}
                        onValueChange={(value) =>
                          setGroupForm({
                            ...groupForm,
                            label_style: {
                              ...groupForm.label_style!,
                              fontWeight: value === "default" ? undefined : value as LabelFontWeight,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="medium">Moyen</SelectItem>
                          <SelectItem value="semibold">Semi-gras</SelectItem>
                          <SelectItem value="bold">Gras</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Font Style */}
                    <div className="space-y-2">
                      <Label htmlFor="group-label-font-style">Style</Label>
                      <Select
                        value={groupForm.label_style.fontStyle || "default"}
                        onValueChange={(value) =>
                          setGroupForm({
                            ...groupForm,
                            label_style: {
                              ...groupForm.label_style!,
                              fontStyle: value === "default" ? undefined : value as LabelFontStyle,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="italic">Italique</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Text Transform */}
                    <div className="space-y-2">
                      <Label htmlFor="group-label-text-transform">Casse</Label>
                      <Select
                        value={groupForm.label_style.textTransform || "default"}
                        onValueChange={(value) =>
                          setGroupForm({
                            ...groupForm,
                            label_style: {
                              ...groupForm.label_style!,
                              textTransform: value === "default" ? undefined : value as LabelTextTransform,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="none">Normal</SelectItem>
                          <SelectItem value="uppercase">MAJUSCULES</SelectItem>
                          <SelectItem value="lowercase">minuscules</SelectItem>
                          <SelectItem value="capitalize">Première Lettre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Color */}
                    <div className="space-y-2">
                      <Label htmlFor="group-label-color">Couleur</Label>
                      <Select
                        value={groupForm.label_style.color || "default"}
                        onValueChange={(value) =>
                          setGroupForm({
                            ...groupForm,
                            label_style: {
                              ...groupForm.label_style!,
                              color: value === "default" ? undefined : value as LabelColor,
                              customColor: value !== "custom" ? undefined : groupForm.label_style?.customColor,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="primary">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-primary" />
                              Principal
                            </span>
                          </SelectItem>
                          <SelectItem value="secondary">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-secondary" />
                              Secondaire
                            </span>
                          </SelectItem>
                          <SelectItem value="muted">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-muted-foreground" />
                              Atténué
                            </span>
                          </SelectItem>
                          <SelectItem value="destructive">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-destructive" />
                              Destructif
                            </span>
                          </SelectItem>
                          <SelectItem value="custom">Personnalisée...</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Custom Color Input */}
                    {groupForm.label_style.color === "custom" && (
                      <div className="space-y-2">
                        <Label htmlFor="group-label-custom-color">Couleur personnalisée</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            id="group-label-custom-color-picker"
                            value={groupForm.label_style.customColor || "#000000"}
                            onChange={(e) =>
                              setGroupForm({
                                ...groupForm,
                                label_style: {
                                  ...groupForm.label_style!,
                                  customColor: e.target.value,
                                },
                              })
                            }
                            className="w-12 h-9 p-1 cursor-pointer"
                          />
                          <Input
                            id="group-label-custom-color"
                            value={groupForm.label_style.customColor || ""}
                            onChange={(e) =>
                              setGroupForm({
                                ...groupForm,
                                label_style: {
                                  ...groupForm.label_style!,
                                  customColor: e.target.value,
                                },
                              })
                            }
                            placeholder="#000000"
                            className="font-mono"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Advanced: Custom Tailwind Classes */}
                  <div className="space-y-2 pt-3 border-t">
                    <Label htmlFor="group-label-custom-class" className="text-xs text-muted-foreground">
                      Classes Tailwind personnalisées (avancé)
                    </Label>
                    <Input
                      id="group-label-custom-class"
                      value={groupForm.label_style.customClassName || ""}
                      onChange={(e) =>
                        setGroupForm({
                          ...groupForm,
                          label_style: {
                            ...groupForm.label_style!,
                            customClassName: e.target.value || undefined,
                          },
                        })
                      }
                      placeholder="Ex: tracking-wide underline"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeGroupDialog}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                isEditingGroup && selectedGroup
                  ? updateGroupMutation.mutate({ id: selectedGroup.id, data: groupForm })
                  : createGroupMutation.mutate(groupForm)
              }
              disabled={createGroupMutation.isPending || updateGroupMutation.isPending || !groupForm.name}
            >
              {isEditingGroup ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field Dialog */}
      <Dialog open={fieldDialogOpen} onOpenChange={closeFieldDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditingField ? "Modifier le champ" : "Nouveau champ"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="field-name">Nom technique</Label>
              <Input
                id="field-name"
                value={fieldForm.name}
                onChange={(e) => handleFieldNameChange(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="field-slug">Slug</Label>
              <Input
                id="field-slug"
                value={fieldForm.slug}
                onChange={(e) => setFieldForm({ ...fieldForm, slug: e.target.value })}
                disabled={isEditingField && (isLoadingSlugUsage || !slugUsageInfo?.can_edit_slug)}
              />
              {isEditingField && isLoadingSlugUsage && (
                <p className="text-xs text-muted-foreground">Vérification des dépendances...</p>
              )}
              {isEditingField && !isLoadingSlugUsage && slugUsageInfo && !slugUsageInfo.can_edit_slug && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-xs text-warning hover:underline cursor-pointer text-left">
                      Champ utilisé, impossible de modifier le slug (voir où)
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Utilisations du slug "{slugUsageInfo.slug}"</h4>
                      <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
                        {slugUsageInfo.usages.map((usage: SlugUsage, index: number) => (
                          <li key={index} className="border-l-2 border-warning pl-2 py-1">
                            <div className="font-medium">{usage.entity_name}</div>
                            <div className="text-xs text-muted-foreground">{usage.description}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="field-label">Libellé</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-xs mb-1 font-medium">Mise en forme disponible :</p>
                    <ul className="text-xs space-y-0.5">
                      <li><code className="bg-muted px-1 rounded">[b]texte[/b]</code> - gras</li>
                      <li><code className="bg-muted px-1 rounded">[i]texte[/i]</code> - italique</li>
                      <li><code className="bg-muted px-1 rounded">[u]texte[/u]</code> - souligné</li>
                      <li><code className="bg-muted px-1 rounded">[s]texte[/s]</code> - barré</li>
                      <li><code className="bg-muted px-1 rounded">[color=#hex]texte[/color]</code> - couleur</li>
                      <li><code className="bg-muted px-1 rounded">[bg=#hex]texte[/bg]</code> - surlignage</li>
                    </ul>
                    <p className="text-xs mt-1 text-muted-foreground">Ex: [b]Sans[/b] option de livraison</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                id="field-label"
                value={fieldForm.label}
                onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="field-type">Type</Label>
              <Select
                value={fieldForm.field_type}
                onValueChange={(value) =>
                  setFieldForm({ ...fieldForm, field_type: value as FieldType })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(fieldTypeLabels).map(([type, label]) => (
                    <SelectItem key={type} value={type}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Group selection */}
            <div className="col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="field-group">Groupe</Label>
                {!isEditingField && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCreateNewGroupInFieldDialog(!createNewGroupInFieldDialog)}
                  >
                    {createNewGroupInFieldDialog ? (
                      <>
                        <X className="h-4 w-4 mr-1" />
                        Annuler
                      </>
                    ) : (
                      <>
                        <FolderPlus className="h-4 w-4 mr-1" />
                        Nouveau groupe
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {createNewGroupInFieldDialog ? (
                <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                  <div className="space-y-2">
                    <Label>Nom du nouveau groupe</Label>
                    <Input
                      value={newGroupInFieldDialog.name}
                      onChange={(e) =>
                        setNewGroupInFieldDialog({
                          ...newGroupInFieldDialog,
                          name: e.target.value,
                          slug: slugify(e.target.value),
                        })
                      }
                      placeholder="Ex: Informations personnelles"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Groupe parent (optionnel)</Label>
                    <Select
                      value={newGroupInFieldDialog.parent_id?.toString() || "none"}
                      onValueChange={(value) =>
                        setNewGroupInFieldDialog({
                          ...newGroupInFieldDialog,
                          parent_id: value !== "none" ? parseInt(value) : null,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Aucun (groupe racine)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun (groupe racine)</SelectItem>
                        {flatGroups.map(({ group, depth }) => (
                          <SelectItem key={group.id} value={group.id.toString()}>
                            {"─".repeat(depth)} {group.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <Select
                  value={fieldForm.group_id?.toString() || ""}
                  onValueChange={(value) =>
                    setFieldForm({ ...fieldForm, group_id: parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un groupe..." />
                  </SelectTrigger>
                  <SelectContent>
                    {flatGroups.map(({ group, depth }) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {"─".repeat(depth)} {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="col-span-2 space-y-2">
              <Label htmlFor="field-description">Description</Label>
              <Input
                id="field-description"
                value={fieldForm.description}
                onChange={(e) => setFieldForm({ ...fieldForm, description: e.target.value })}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="field-implementation-note">Note d&apos;implémentation</Label>
              <Textarea
                id="field-implementation-note"
                value={fieldForm.implementation_note}
                onChange={(e) => setFieldForm({ ...fieldForm, implementation_note: e.target.value })}
                placeholder="Notes techniques pour les développeurs..."
                rows={2}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="field-excel-source" className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-success" />
                Source Excel
              </Label>
              <Input
                id="field-excel-source"
                value={fieldForm.excel_source}
                onChange={(e) => setFieldForm({ ...fieldForm, excel_source: e.target.value })}
                placeholder="Ex: 'Costing Transport 2025'!H7"
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="field-placeholder">Placeholder</Label>
              <Input
                id="field-placeholder"
                value={fieldForm.placeholder}
                onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="field-order">Ordre au sein du groupe</Label>
              <Input
                id="field-order"
                type="number"
                value={fieldForm.order}
                onChange={(e) => setFieldForm({ ...fieldForm, order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center space-x-2 pt-6">
              <Switch
                id="field-required"
                checked={fieldForm.required}
                onCheckedChange={(checked) => setFieldForm({ ...fieldForm, required: checked })}
              />
              <Label htmlFor="field-required">Champ requis</Label>
            </div>

            {/* Conditional Required Section */}
            <div className="col-span-2 space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                    <path d="M3.6 9h16.8" />
                    <path d="M12 3 2 9l10 6 10-6-10-6Z" />
                    <path d="M2 9v8l10 6" />
                    <path d="M22 9v8l-10 6" />
                  </svg>
                  <Label>Requis conditionnel</Label>
                </div>
                {!fieldForm.conditional_required ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFieldForm({
                        ...fieldForm,
                        conditional_required: {
                          logic: "OR",
                          conditions: [{ field_slug: "", operator: "equals", value: "" }],
                          message: "",
                        },
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter des conditions
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFieldForm({ ...fieldForm, conditional_required: null })}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                )}
              </div>

              {fieldForm.conditional_required && (
                <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Ce champ devient requis si</span>
                    <Select
                      value={fieldForm.conditional_required.logic}
                      onValueChange={(value: ConditionLogic) =>
                        setFieldForm({
                          ...fieldForm,
                          conditional_required: {
                            ...fieldForm.conditional_required!,
                            logic: value,
                          },
                        })
                      }
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">TOUTES</SelectItem>
                        <SelectItem value="OR">AU MOINS UNE</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">des conditions sont remplies :</span>
                  </div>

                  <div className="space-y-2">
                    {fieldForm.conditional_required.conditions.map((condition, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <FieldSlugSelector
                          value={condition.field_slug}
                          onSelect={(slug) => {
                            const newConditions = [...fieldForm.conditional_required!.conditions];
                            newConditions[index] = { ...newConditions[index], field_slug: slug };
                            setFieldForm({
                              ...fieldForm,
                              conditional_required: {
                                ...fieldForm.conditional_required!,
                                conditions: newConditions,
                              },
                            });
                          }}
                          fields={fieldsWithFullPath}
                          excludeSlug={fieldForm.slug}
                          placeholder="Champ..."
                          className="flex-1"
                        />

                        <Select
                          value={condition.operator}
                          onValueChange={(value: ConditionOperator) => {
                            const newConditions = [...fieldForm.conditional_required!.conditions];
                            newConditions[index] = { ...newConditions[index], operator: value };
                            setFieldForm({
                              ...fieldForm,
                              conditional_required: {
                                ...fieldForm.conditional_required!,
                                conditions: newConditions,
                              },
                            });
                          }}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(operatorLabels).map(([op, label]) => (
                              <SelectItem key={op} value={op}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {!["is_empty", "is_not_empty"].includes(condition.operator) && (
                          (() => {
                            const selectedField = allFields.find(f => f.slug === condition.field_slug);
                            const isCheckbox = selectedField?.field_type === "checkbox";
                            const valueOptions = getConditionValueOptions(selectedField);
                            const hasOptions = valueOptions.length > 0;
                            const showSelectForValue = ["equals", "not_equals", "in", "not_in"].includes(condition.operator);
                            
                            if (isCheckbox) {
                              return (
                                <Select
                                  value={String(condition.value ?? "")}
                                  onValueChange={(value) => {
                                    const newConditions = [...fieldForm.conditional_required!.conditions];
                                    newConditions[index] = { ...newConditions[index], value };
                                    setFieldForm({
                                      ...fieldForm,
                                      conditional_required: {
                                        ...fieldForm.conditional_required!,
                                        conditions: newConditions,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="État..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="true">Coché</SelectItem>
                                    <SelectItem value="false">Décoché</SelectItem>
                                  </SelectContent>
                                </Select>
                              );
                            }
                            
                            if (hasOptions && showSelectForValue) {
                              return (
                                <Select
                                  value={String(condition.value ?? "")}
                                  onValueChange={(value) => {
                                    const newConditions = [...fieldForm.conditional_required!.conditions];
                                    newConditions[index] = { ...newConditions[index], value };
                                    setFieldForm({
                                      ...fieldForm,
                                      conditional_required: {
                                        ...fieldForm.conditional_required!,
                                        conditions: newConditions,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Sélectionner une valeur..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {valueOptions.map((opt) => (
                                      <SelectItem key={opt} value={opt}>
                                        {opt}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              );
                            }
                            
                            return (
                              <Input
                                className="flex-1"
                                placeholder="Valeur..."
                                value={String(condition.value ?? "")}
                                onChange={(e) => {
                                  const newConditions = [...fieldForm.conditional_required!.conditions];
                                  newConditions[index] = { ...newConditions[index], value: e.target.value };
                                  setFieldForm({
                                    ...fieldForm,
                                    conditional_required: {
                                      ...fieldForm.conditional_required!,
                                      conditions: newConditions,
                                    },
                                  });
                                }}
                              />
                            );
                          })()
                        )}

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newConditions = fieldForm.conditional_required!.conditions.filter(
                              (_, i) => i !== index
                            );
                            if (newConditions.length === 0) {
                              setFieldForm({ ...fieldForm, conditional_required: null });
                            } else {
                              setFieldForm({
                                ...fieldForm,
                                conditional_required: {
                                  ...fieldForm.conditional_required!,
                                  conditions: newConditions,
                                },
                              });
                            }
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFieldForm({
                        ...fieldForm,
                        conditional_required: {
                          ...fieldForm.conditional_required!,
                          conditions: [
                            ...fieldForm.conditional_required!.conditions,
                            { field_slug: "", operator: "equals", value: "" },
                          ],
                        },
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une condition
                  </Button>

                  <div className="space-y-2 border-t pt-3">
                    <Label htmlFor="conditional-required-message">Message d&apos;erreur personnalisé</Label>
                    <Input
                      id="conditional-required-message"
                      placeholder="Message affiché si le champ n'est pas rempli..."
                      value={fieldForm.conditional_required.message || ""}
                      onChange={(e) =>
                        setFieldForm({
                          ...fieldForm,
                          conditional_required: {
                            ...fieldForm.conditional_required!,
                            message: e.target.value || undefined,
                          },
                        })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Laissez vide pour utiliser le message par défaut : &quot;Ce champ est requis&quot;
                    </p>
                  </div>
                </div>
              )}
            </div>

            {fieldForm.field_type === "number" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="field-min">Valeur minimum</Label>
                  <Input
                    id="field-min"
                    type="number"
                    value={fieldForm.min_value ?? ""}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, min_value: e.target.value ? parseFloat(e.target.value) : undefined })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="field-max">Valeur maximum</Label>
                  <Input
                    id="field-max"
                    type="number"
                    value={fieldForm.max_value ?? ""}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, max_value: e.target.value ? parseFloat(e.target.value) : undefined })
                    }
                  />
                </div>
                <div className="col-span-2 flex items-center space-x-2 pt-2">
                  <Switch
                    id="field-integer-only"
                    checked={fieldForm.integer_only}
                    onCheckedChange={(checked) => setFieldForm({ ...fieldForm, integer_only: checked })}
                  />
                  <div className="space-y-0.5">
                    <Label htmlFor="field-integer-only">Entier uniquement</Label>
                    <p className="text-xs text-muted-foreground">
                      Bloque la saisie de décimales (virgule / point) dans le simulateur.
                    </p>
                  </div>
                </div>
                <div className="col-span-2 pt-4 border-t">
                  <Label className="text-muted-foreground text-xs">
                    Valeurs préconisées (avertissement uniquement, ne bloque pas le calcul)
                  </Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="field-rec-min">Minimum préconisé</Label>
                  <Input
                    id="field-rec-min"
                    type="number"
                    value={fieldForm.recommended_min_value ?? ""}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, recommended_min_value: e.target.value ? parseFloat(e.target.value) : undefined })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="field-rec-max">Maximum préconisé</Label>
                  <Input
                    id="field-rec-max"
                    type="number"
                    value={fieldForm.recommended_max_value ?? ""}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, recommended_max_value: e.target.value ? parseFloat(e.target.value) : undefined })
                    }
                  />
                </div>
                {(fieldForm.recommended_min_value !== undefined || fieldForm.recommended_max_value !== undefined) && (
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="field-rec-warning">Message d&apos;avertissement personnalisé</Label>
                    <Textarea
                      id="field-rec-warning"
                      placeholder="Ex: merci de contacter G. LAPUENTE pour dérogation"
                      value={fieldForm.recommended_warning_message ?? ""}
                      onChange={(e) =>
                        setFieldForm({ ...fieldForm, recommended_warning_message: e.target.value || undefined })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Laissez vide pour utiliser le message par défaut
                    </p>
                  </div>
                )}
              </>
            )}

            {(fieldForm.field_type === "select" || fieldForm.field_type === "multiselect" || fieldForm.field_type === "radio") && (
              <>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="field-enum">Options</Label>
                  <div className="flex gap-2">
                    <Input
                      id="field-enum"
                      value={newEnumValue}
                      onChange={(e) => setNewEnumValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newEnumValue.trim()) {
                          e.preventDefault();
                          setFieldForm({ ...fieldForm, enum_values: [...fieldForm.enum_values, newEnumValue.trim()] });
                          setNewEnumValue("");
                        }
                      }}
                      placeholder="Saisir une option..."
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        if (newEnumValue.trim()) {
                          setFieldForm({ ...fieldForm, enum_values: [...fieldForm.enum_values, newEnumValue.trim()] });
                          setNewEnumValue("");
                        }
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {fieldForm.enum_values.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {fieldForm.enum_values.map((value, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1 pl-3 pr-1 py-1">
                          {value}
                          <button
                            type="button"
                            onClick={() => {
                              setFieldForm({
                                ...fieldForm,
                                enum_values: fieldForm.enum_values.filter((_, i) => i !== index),
                              });
                            }}
                            className="ml-1 hover:bg-muted rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="field-ref">Ou table de référence</Label>
                  <Select
                    value={fieldForm.reference_table_id?.toString() || "none"}
                    onValueChange={(value) =>
                      setFieldForm({ 
                        ...fieldForm, 
                        reference_table_id: value !== "none" ? parseInt(value) : undefined,
                        reference_column: "", // Reset column when table changes
                        reference_value_column: "", // Reset value column when table changes
                        reference_filter: null, // Reset filter when table changes
                        default_value: "" // Reset default value when table changes
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {references?.items.map((ref) => (
                        <SelectItem key={ref.id} value={ref.id.toString()}>
                          {ref.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Reference column configuration - only show when reference table is selected */}
                {fieldForm.reference_table_id && (
                  <div className="col-span-2 space-y-3 p-3 rounded-lg border bg-info/10">
                    <div className="space-y-2">
                      <Label htmlFor="field-ref-column" className="text-sm font-medium">
                        Colonne de référence (affichage) *
                      </Label>
                      <Select
                        value={fieldForm.reference_column || "none"}
                        onValueChange={(value) =>
                          setFieldForm({
                            ...fieldForm,
                            reference_column: value !== "none" ? value : "",
                          })
                        }
                      >
                        <SelectTrigger id="field-ref-column">
                          <SelectValue placeholder="Sélectionner la colonne..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucune</SelectItem>
                          {references?.items
                            .find((ref) => ref.id === fieldForm.reference_table_id)
                            ?.columns.map((col) => (
                              <SelectItem key={col.name} value={col.name}>
                                {col.name} ({col.type})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Colonne qui sera affichée dans le dropdown (ex: "taille", "nom", etc.)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="field-ref-value" className="text-sm font-medium">
                        Colonne de valeur (optionnel)
                      </Label>
                      <Select
                        value={fieldForm.reference_value_column || "none"}
                        onValueChange={(value) =>
                          setFieldForm({
                            ...fieldForm,
                            reference_value_column: value !== "none" ? value : "",
                          })
                        }
                      >
                        <SelectTrigger id="field-ref-value">
                          <SelectValue placeholder="Même que colonne d'affichage" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Même que colonne d'affichage</SelectItem>
                          {references?.items
                            .find((ref) => ref.id === fieldForm.reference_table_id)
                            ?.columns.map((col) => (
                              <SelectItem key={col.name} value={col.name}>
                                {col.name} ({col.type})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Colonne utilisée comme valeur interne (laissez vide pour utiliser la même que l'affichage)
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Filter section - only show when reference table is selected */}
                {fieldForm.reference_table_id && (
                  <div className="col-span-2 space-y-3 p-3 rounded-lg border bg-muted/30">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Filtrer les options</Label>
                      {!fieldForm.reference_filter ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setFieldForm({
                              ...fieldForm,
                              reference_filter: { column: "", values: [] },
                            })
                          }
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Ajouter un filtre
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFieldForm({ ...fieldForm, reference_filter: null, default_value: "" })}
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Supprimer
                        </Button>
                      )}
                    </div>
                    
                    {fieldForm.reference_filter && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Filtrer par colonne</Label>
                            <Select
                              value={fieldForm.reference_filter.column || "none"}
                              onValueChange={(value) =>
                                setFieldForm({
                                  ...fieldForm,
                                  reference_filter: {
                                    ...fieldForm.reference_filter!,
                                    column: value !== "none" ? value : "",
                                    values: [], // Reset values when column changes
                                  },
                                  default_value: "", // Reset default value
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Colonne..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Sélectionner...</SelectItem>
                                {references?.items
                                  .find((ref) => ref.id === fieldForm.reference_table_id)
                                  ?.columns.map((col) => (
                                    <SelectItem key={col.name} value={col.name}>
                                      {col.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Valeurs autorisées</Label>
                            <Select
                              value="select-values"
                              onValueChange={(value) => {
                                if (value !== "select-values" && fieldForm.reference_filter) {
                                  const newValues = fieldForm.reference_filter.values.includes(value)
                                    ? fieldForm.reference_filter.values.filter((v) => v !== value)
                                    : [...fieldForm.reference_filter.values, value];
                                  setFieldForm({
                                    ...fieldForm,
                                    reference_filter: {
                                      ...fieldForm.reference_filter,
                                      values: newValues,
                                    },
                                    default_value: "", // Reset default value when filter changes
                                  });
                                }
                              }}
                              disabled={!fieldForm.reference_filter.column}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={fieldForm.reference_filter.values.length > 0 ? `${fieldForm.reference_filter.values.length} sélectionnée(s)` : "Sélectionner..."} />
                              </SelectTrigger>
                              <SelectContent>
                                {(() => {
                                  const selectedTable = references?.items.find(
                                    (ref) => ref.id === fieldForm.reference_table_id
                                  );
                                  if (!selectedTable?.data || !fieldForm.reference_filter?.column) return null;
                                  
                                  // Get unique values from the filter column
                                  const uniqueValues = Array.from(new Set(
                                    selectedTable.data
                                      .map((row) => row[fieldForm.reference_filter!.column])
                                      .filter((val): val is string | number => val !== null && val !== undefined)
                                      .map((val) => String(val))
                                  ));
                                  
                                  return uniqueValues.map((value) => (
                                    <SelectItem key={value} value={value}>
                                      <div className="flex items-center gap-2">
                                        {fieldForm.reference_filter?.values.includes(value) && (
                                          <Check className="h-3 w-3 text-primary" />
                                        )}
                                        {value}
                                      </div>
                                    </SelectItem>
                                  ));
                                })()}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Display selected filter values */}
                        {fieldForm.reference_filter.values.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {fieldForm.reference_filter.values.map((value) => (
                              <Badge
                                key={value}
                                variant="secondary"
                                className="flex items-center gap-1"
                              >
                                {value}
                                <X
                                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                                  onClick={() =>
                                    setFieldForm({
                                      ...fieldForm,
                                      reference_filter: {
                                        ...fieldForm.reference_filter!,
                                        values: fieldForm.reference_filter!.values.filter((v) => v !== value),
                                      },
                                      default_value: "",
                                    })
                                  }
                                />
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="col-span-2 space-y-2">
              <Label htmlFor="field-default">Valeur par défaut</Label>
              {fieldForm.field_type === "checkbox" ? (
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    id="field-default"
                    checked={fieldForm.default_value === "true"}
                    onCheckedChange={(checked) => 
                      setFieldForm({ ...fieldForm, default_value: checked ? "true" : "false" })
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    {fieldForm.default_value === "true" ? "Coché" : "Décoché"}
                  </span>
                </div>
              ) : fieldForm.reference_table_id ? (
                <Select
                  value={fieldForm.default_value || "none"}
                  onValueChange={(value) =>
                    setFieldForm({ ...fieldForm, default_value: value !== "none" ? value : "" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une valeur par défaut..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {(() => {
                      const selectedTable = references?.items.find(
                        (ref) => ref.id === fieldForm.reference_table_id
                      );
                      if (!selectedTable?.data) return null;
                      
                      // Apply filter if defined
                      let filteredData = selectedTable.data;
                      if (fieldForm.reference_filter?.column && fieldForm.reference_filter.values.length > 0) {
                        filteredData = selectedTable.data.filter((row) => {
                          const cellValue = String(row[fieldForm.reference_filter!.column] ?? "");
                          return fieldForm.reference_filter!.values.includes(cellValue);
                        });
                      }
                      
                      // Get unique values from the display column
                      const uniqueValues = Array.from(new Set(
                        filteredData
                          .map((row) => row[fieldForm.reference_column])
                          .filter((val): val is string | number => val !== null && val !== undefined)
                          .map((val) => String(val))
                      ));
                      
                      return uniqueValues.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="field-default"
                  value={fieldForm.default_value}
                  onChange={(e) => setFieldForm({ ...fieldForm, default_value: e.target.value })}
                />
              )}
            </div>

            <div className="col-span-2 space-y-2">
              <Label htmlFor="field-tooltip">Info-bulle</Label>
              <Textarea
                id="field-tooltip"
                value={fieldForm.tooltip}
                onChange={(e) => setFieldForm({ ...fieldForm, tooltip: e.target.value })}
                rows={2}
              />
            </div>

            {/* Visibility Conditions Section */}
            <div className="col-span-2 space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  <Label>Conditions de visibilité</Label>
                </div>
                {!fieldForm.visibility_conditions ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFieldForm({
                        ...fieldForm,
                        visibility_conditions: {
                          logic: "AND",
                          conditions: [{ field_slug: "", operator: "equals", value: "" }],
                        },
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter des conditions
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFieldForm({ ...fieldForm, visibility_conditions: null })}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                )}
              </div>

              {fieldForm.visibility_conditions && (
                <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Afficher ce champ si</span>
                    <Select
                      value={fieldForm.visibility_conditions.logic}
                      onValueChange={(value: ConditionLogic) =>
                        setFieldForm({
                          ...fieldForm,
                          visibility_conditions: {
                            ...fieldForm.visibility_conditions!,
                            logic: value,
                          },
                        })
                      }
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">TOUTES</SelectItem>
                        <SelectItem value="OR">AU MOINS UNE</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">des conditions sont remplies :</span>
                  </div>

                  <div className="space-y-2">
                    {fieldForm.visibility_conditions.conditions.map((condition, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <FieldSlugSelector
                          value={condition.field_slug}
                          onSelect={(slug) => {
                            const newConditions = [...fieldForm.visibility_conditions!.conditions];
                            newConditions[index] = { ...newConditions[index], field_slug: slug };
                            setFieldForm({
                              ...fieldForm,
                              visibility_conditions: {
                                ...fieldForm.visibility_conditions!,
                                conditions: newConditions,
                              },
                            });
                          }}
                          fields={fieldsWithFullPath}
                          excludeSlug={fieldForm.slug}
                          placeholder="Champ..."
                          className="flex-1"
                        />

                        <Select
                          value={condition.operator}
                          onValueChange={(value: ConditionOperator) => {
                            const newConditions = [...fieldForm.visibility_conditions!.conditions];
                            newConditions[index] = { ...newConditions[index], operator: value };
                            setFieldForm({
                              ...fieldForm,
                              visibility_conditions: {
                                ...fieldForm.visibility_conditions!,
                                conditions: newConditions,
                              },
                            });
                          }}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(operatorLabels).map(([op, label]) => (
                              <SelectItem key={op} value={op}>
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {!["is_empty", "is_not_empty"].includes(condition.operator) && (
                          (() => {
                            const selectedField = allFields.find(f => f.slug === condition.field_slug);
                            const isCheckbox = selectedField?.field_type === "checkbox";
                            const valueOptions = getConditionValueOptions(selectedField);
                            const hasOptions = valueOptions.length > 0;
                            const showSelectForValue = ["equals", "not_equals", "in", "not_in"].includes(condition.operator);
                            
                            if (isCheckbox) {
                              return (
                                <Select
                                  value={String(condition.value ?? "")}
                                  onValueChange={(value) => {
                                    const newConditions = [...fieldForm.visibility_conditions!.conditions];
                                    newConditions[index] = { ...newConditions[index], value };
                                    setFieldForm({
                                      ...fieldForm,
                                      visibility_conditions: {
                                        ...fieldForm.visibility_conditions!,
                                        conditions: newConditions,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="État..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="true">Coché</SelectItem>
                                    <SelectItem value="false">Décoché</SelectItem>
                                  </SelectContent>
                                </Select>
                              );
                            }
                            
                            if (hasOptions && showSelectForValue) {
                              return (
                                <Select
                                  value={String(condition.value ?? "")}
                                  onValueChange={(value) => {
                                    const newConditions = [...fieldForm.visibility_conditions!.conditions];
                                    newConditions[index] = { ...newConditions[index], value };
                                    setFieldForm({
                                      ...fieldForm,
                                      visibility_conditions: {
                                        ...fieldForm.visibility_conditions!,
                                        conditions: newConditions,
                                      },
                                    });
                                  }}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Sélectionner une valeur..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {valueOptions.map((opt) => (
                                      <SelectItem key={opt} value={opt}>
                                        {opt}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              );
                            }
                            
                            return (
                              <Input
                                className="flex-1"
                                placeholder="Valeur..."
                                value={String(condition.value ?? "")}
                                onChange={(e) => {
                                  const newConditions = [...fieldForm.visibility_conditions!.conditions];
                                  newConditions[index] = { ...newConditions[index], value: e.target.value };
                                  setFieldForm({
                                    ...fieldForm,
                                    visibility_conditions: {
                                      ...fieldForm.visibility_conditions!,
                                      conditions: newConditions,
                                    },
                                  });
                                }}
                              />
                            );
                          })()
                        )}

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newConditions = fieldForm.visibility_conditions!.conditions.filter(
                              (_, i) => i !== index
                            );
                            if (newConditions.length === 0) {
                              setFieldForm({ ...fieldForm, visibility_conditions: null });
                            } else {
                              setFieldForm({
                                ...fieldForm,
                                visibility_conditions: {
                                  ...fieldForm.visibility_conditions!,
                                  conditions: newConditions,
                                },
                              });
                            }
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFieldForm({
                        ...fieldForm,
                        visibility_conditions: {
                          ...fieldForm.visibility_conditions!,
                          conditions: [
                            ...fieldForm.visibility_conditions!.conditions,
                            { field_slug: "", operator: "equals", value: "" },
                          ],
                        },
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une condition
                  </Button>
                </div>
              )}
            </div>

            {/* Conditional Constraints Section - only for number, select, multiselect and radio */}
            {(fieldForm.field_type === "number" || fieldForm.field_type === "select" || fieldForm.field_type === "multiselect" || fieldForm.field_type === "radio") && (
              <div className="col-span-2 space-y-4 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 3h5v5" />
                      <path d="M8 3H3v5" />
                      <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" />
                      <path d="m15 9 6-6" />
                    </svg>
                    <Label>Contraintes conditionnelles</Label>
                  </div>
                  {!fieldForm.conditional_constraints ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFieldForm({
                          ...fieldForm,
                          conditional_constraints: {
                            source_field_slug: "",
                            rules: [{ when_value: "", ...(fieldForm.field_type === "number" ? { min_value: undefined, max_value: undefined } : { allowed_values: [] }) }],
                          },
                        })
                      }
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter des contraintes
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFieldForm({ ...fieldForm, conditional_constraints: null })}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  )}
                </div>

                {fieldForm.conditional_constraints && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                    <div className="space-y-2">
                      <Label className="text-sm">Champ source</Label>
                      <FieldSlugSelector
                        value={fieldForm.conditional_constraints.source_field_slug || ""}
                        onSelect={(slug) =>
                          setFieldForm({
                            ...fieldForm,
                            conditional_constraints: {
                              ...fieldForm.conditional_constraints!,
                              source_field_slug: slug,
                            },
                          })
                        }
                        fields={fieldsWithFullPath}
                        excludeSlug={fieldForm.slug}
                        filterTypes={["select", "radio", "number", "text", "checkbox"]}
                        placeholder="Sélectionner un champ..."
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Les contraintes de ce champ dépendront de la valeur du champ source
                      </p>
                    </div>

                    {fieldForm.conditional_constraints.source_field_slug && (
                      <>
                        <div className="border-t pt-3">
                          <Label className="text-sm mb-2 block">Règles</Label>
                          <div className="space-y-3">
                            {fieldForm.conditional_constraints.rules.map((rule, index) => (
                              <div key={index} className="flex items-start gap-2 p-3 rounded border bg-background">
                                <div className="flex-1 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground whitespace-nowrap">Si</span>
                                    <code className="text-xs px-1 py-0.5 bg-muted rounded">
                                      {fieldsWithFullPath.find(f => f.field.slug === fieldForm.conditional_constraints!.source_field_slug)?.fullPath || fieldForm.conditional_constraints!.source_field_slug}
                                    </code>
                                    <span className="text-sm text-muted-foreground">=</span>
                                    {(() => {
                                      const sourceField = allFields.find(f => f.slug === fieldForm.conditional_constraints!.source_field_slug);
                                      // Handle checkbox source field
                                      if (sourceField?.field_type === "checkbox") {
                                        return (
                                          <Select
                                            value={rule.when_value === true ? "true" : rule.when_value === false ? "false" : String(rule.when_value) || ""}
                                            onValueChange={(value) => {
                                              const newRules = [...fieldForm.conditional_constraints!.rules];
                                              newRules[index] = { ...newRules[index], when_value: value === "true" };
                                              setFieldForm({
                                                ...fieldForm,
                                                conditional_constraints: {
                                                  ...fieldForm.conditional_constraints!,
                                                  rules: newRules,
                                                },
                                              });
                                            }}
                                          >
                                            <SelectTrigger className="w-40">
                                              <SelectValue placeholder="Valeur..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="true">Coché (Oui)</SelectItem>
                                              <SelectItem value="false">Non coché (Non)</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        );
                                      }
                                      // Handle select/radio source field with enum_values
                                      if ((sourceField?.field_type === "select" || sourceField?.field_type === "radio") && sourceField.enum_values && sourceField.enum_values.length > 0) {
                                        return (
                                          <Select
                                            value={String(rule.when_value) || ""}
                                            onValueChange={(value) => {
                                              const newRules = [...fieldForm.conditional_constraints!.rules];
                                              newRules[index] = { ...newRules[index], when_value: value };
                                              setFieldForm({
                                                ...fieldForm,
                                                conditional_constraints: {
                                                  ...fieldForm.conditional_constraints!,
                                                  rules: newRules,
                                                },
                                              });
                                            }}
                                          >
                                            <SelectTrigger className="w-40">
                                              <SelectValue placeholder="Valeur..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {sourceField.enum_values.map((v) => (
                                                <SelectItem key={v} value={v}>{v}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        );
                                      }
                                      return (
                                        <Input
                                          className="w-40"
                                          placeholder="Valeur..."
                                          value={String(rule.when_value) || ""}
                                          onChange={(e) => {
                                            const newRules = [...fieldForm.conditional_constraints!.rules];
                                            newRules[index] = { ...newRules[index], when_value: e.target.value };
                                            setFieldForm({
                                              ...fieldForm,
                                              conditional_constraints: {
                                                ...fieldForm.conditional_constraints!,
                                                rules: newRules,
                                              },
                                            });
                                          }}
                                        />
                                      );
                                    })()}
                                  </div>

                                  {fieldForm.field_type === "number" && (
                                    <div className="flex items-center gap-2 pl-6">
                                      <span className="text-sm text-muted-foreground">→</span>
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs">Min:</Label>
                                        <Input
                                          type="number"
                                          className="w-24"
                                          placeholder="—"
                                          value={rule.min_value ?? ""}
                                          onChange={(e) => {
                                            const newRules = [...fieldForm.conditional_constraints!.rules];
                                            newRules[index] = { 
                                              ...newRules[index], 
                                              min_value: e.target.value ? parseFloat(e.target.value) : undefined 
                                            };
                                            setFieldForm({
                                              ...fieldForm,
                                              conditional_constraints: {
                                                ...fieldForm.conditional_constraints!,
                                                rules: newRules,
                                              },
                                            });
                                          }}
                                        />
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs">Max:</Label>
                                        <Input
                                          type="number"
                                          className="w-24"
                                          placeholder="—"
                                          value={rule.max_value ?? ""}
                                          onChange={(e) => {
                                            const newRules = [...fieldForm.conditional_constraints!.rules];
                                            newRules[index] = { 
                                              ...newRules[index], 
                                              max_value: e.target.value ? parseFloat(e.target.value) : undefined 
                                            };
                                            setFieldForm({
                                              ...fieldForm,
                                              conditional_constraints: {
                                                ...fieldForm.conditional_constraints!,
                                                rules: newRules,
                                              },
                                            });
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {(fieldForm.field_type === "select" || fieldForm.field_type === "multiselect" || fieldForm.field_type === "radio") && (
                                    <div className="flex items-start gap-2 pl-6">
                                      <span className="text-sm text-muted-foreground mt-2">→</span>
                                      <div className="flex-1 space-y-3">
                                        {/* Mode selection: filter options OR use different reference table */}
                                        <div className="flex items-center gap-4">
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="radio"
                                              name={`constraint-mode-${index}`}
                                              checked={!rule.reference_table_id}
                                              onChange={() => {
                                                const newRules = [...fieldForm.conditional_constraints!.rules];
                                                newRules[index] = { 
                                                  ...newRules[index], 
                                                  reference_table_id: undefined,
                                                  reference_column: undefined,
                                                  reference_value_column: undefined,
                                                };
                                                setFieldForm({
                                                  ...fieldForm,
                                                  conditional_constraints: {
                                                    ...fieldForm.conditional_constraints!,
                                                    rules: newRules,
                                                  },
                                                });
                                              }}
                                              className="h-4 w-4"
                                            />
                                            <span className="text-xs">Filtrer les options</span>
                                          </label>
                                          <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="radio"
                                              name={`constraint-mode-${index}`}
                                              checked={!!rule.reference_table_id}
                                              onChange={() => {
                                                const newRules = [...fieldForm.conditional_constraints!.rules];
                                                newRules[index] = { 
                                                  ...newRules[index], 
                                                  allowed_values: undefined,
                                                  reference_table_id: references?.items?.[0]?.id,
                                                  reference_column: "",
                                                  reference_value_column: "",
                                                };
                                                setFieldForm({
                                                  ...fieldForm,
                                                  conditional_constraints: {
                                                    ...fieldForm.conditional_constraints!,
                                                    rules: newRules,
                                                  },
                                                });
                                              }}
                                              className="h-4 w-4"
                                            />
                                            <span className="text-xs">Utiliser une autre table de référence</span>
                                          </label>
                                        </div>

                                        {/* Filter mode: allowed_values */}
                                        {!rule.reference_table_id && (
                                          <>
                                            <Label className="text-xs">Options autorisées:</Label>
                                            {/* Get available options from the field itself */}
                                            {(() => {
                                              const availableOptions = fieldForm.enum_values.length > 0 
                                                ? fieldForm.enum_values 
                                                : (() => {
                                                    // Get from reference table if available
                                                    if (fieldForm.reference_table_id && references?.items) {
                                                      const table = references.items.find((t) => t.id === fieldForm.reference_table_id);
                                                      if (table?.data) {
                                                        let filteredData = table.data;
                                                        if (fieldForm.reference_filter?.column && fieldForm.reference_filter.values.length > 0) {
                                                          filteredData = table.data.filter((row) => {
                                                            const cellValue = String(row[fieldForm.reference_filter!.column] ?? "");
                                                            return fieldForm.reference_filter!.values.includes(cellValue);
                                                          });
                                                        }
                                                        return Array.from(new Set(
                                                          filteredData
                                                            .map((row) => row[fieldForm.reference_column])
                                                            .filter((val): val is string | number => val !== null && val !== undefined)
                                                            .map((val) => String(val))
                                                        ));
                                                      }
                                                    }
                                                    return [];
                                                  })();

                                              if (availableOptions.length > 0) {
                                                return (
                                                  <div className="flex flex-wrap gap-2">
                                                    {availableOptions.map((option) => {
                                                      const isSelected = rule.allowed_values?.includes(option);
                                                      return (
                                                        <Badge
                                                          key={option}
                                                          variant={isSelected ? "default" : "outline"}
                                                          className={`cursor-pointer transition-colors ${isSelected ? "" : "hover:bg-muted"}`}
                                                          onClick={() => {
                                                            const newRules = [...fieldForm.conditional_constraints!.rules];
                                                            const currentAllowed = rule.allowed_values || [];
                                                            newRules[index] = {
                                                              ...newRules[index],
                                                              allowed_values: isSelected
                                                                ? currentAllowed.filter((v) => v !== option)
                                                                : [...currentAllowed, option],
                                                            };
                                                            setFieldForm({
                                                              ...fieldForm,
                                                              conditional_constraints: {
                                                                ...fieldForm.conditional_constraints!,
                                                                rules: newRules,
                                                              },
                                                            });
                                                          }}
                                                        >
                                                          {isSelected && <Check className="h-3 w-3 mr-1" />}
                                                          {option}
                                                        </Badge>
                                                      );
                                                    })}
                                                  </div>
                                                );
                                              }
                                              return <p className="text-xs text-muted-foreground">Définissez d&apos;abord les options du champ (enum ou table de référence)</p>;
                                            })()}
                                          </>
                                        )}

                                        {/* Reference table mode */}
                                        {rule.reference_table_id && (
                                          <div className="space-y-2 p-3 rounded border bg-info/10">
                                            <div className="space-y-1">
                                              <Label className="text-xs">Table de référence</Label>
                                              <Select
                                                value={rule.reference_table_id?.toString() || ""}
                                                onValueChange={(value) => {
                                                  const newRules = [...fieldForm.conditional_constraints!.rules];
                                                  newRules[index] = { 
                                                    ...newRules[index], 
                                                    reference_table_id: parseInt(value),
                                                    reference_column: "",
                                                    reference_value_column: "",
                                                  };
                                                  setFieldForm({
                                                    ...fieldForm,
                                                    conditional_constraints: {
                                                      ...fieldForm.conditional_constraints!,
                                                      rules: newRules,
                                                    },
                                                  });
                                                }}
                                              >
                                                <SelectTrigger className="w-full">
                                                  <SelectValue placeholder="Sélectionner une table..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {references?.items.map((ref) => (
                                                    <SelectItem key={ref.id} value={ref.id.toString()}>
                                                      {ref.name}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            
                                            {rule.reference_table_id && (
                                              <>
                                                <div className="space-y-1">
                                                  <Label className="text-xs">Colonne d&apos;affichage *</Label>
                                                  <Select
                                                    value={rule.reference_column || "none"}
                                                    onValueChange={(value) => {
                                                      const newRules = [...fieldForm.conditional_constraints!.rules];
                                                      newRules[index] = { 
                                                        ...newRules[index], 
                                                        reference_column: value !== "none" ? value : "",
                                                      };
                                                      setFieldForm({
                                                        ...fieldForm,
                                                        conditional_constraints: {
                                                          ...fieldForm.conditional_constraints!,
                                                          rules: newRules,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <SelectTrigger className="w-full">
                                                      <SelectValue placeholder="Colonne..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="none">Sélectionner...</SelectItem>
                                                      {references?.items
                                                        .find((ref) => ref.id === rule.reference_table_id)
                                                        ?.columns.map((col) => (
                                                          <SelectItem key={col.name} value={col.name}>
                                                            {col.name} ({col.type})
                                                          </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                  </Select>
                                                </div>

                                                <div className="space-y-1">
                                                  <Label className="text-xs">Colonne de valeur (optionnel)</Label>
                                                  <Select
                                                    value={rule.reference_value_column || "none"}
                                                    onValueChange={(value) => {
                                                      const newRules = [...fieldForm.conditional_constraints!.rules];
                                                      newRules[index] = { 
                                                        ...newRules[index], 
                                                        reference_value_column: value !== "none" ? value : "",
                                                      };
                                                      setFieldForm({
                                                        ...fieldForm,
                                                        conditional_constraints: {
                                                          ...fieldForm.conditional_constraints!,
                                                          rules: newRules,
                                                        },
                                                      });
                                                    }}
                                                  >
                                                    <SelectTrigger className="w-full">
                                                      <SelectValue placeholder="Même que colonne d'affichage" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="none">Même que colonne d&apos;affichage</SelectItem>
                                                      {references?.items
                                                        .find((ref) => ref.id === rule.reference_table_id)
                                                        ?.columns.map((col) => (
                                                          <SelectItem key={col.name} value={col.name}>
                                                            {col.name} ({col.type})
                                                          </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="shrink-0"
                                  onClick={() => {
                                    const newRules = fieldForm.conditional_constraints!.rules.filter((_, i) => i !== index);
                                    if (newRules.length === 0) {
                                      setFieldForm({ ...fieldForm, conditional_constraints: null });
                                    } else {
                                      setFieldForm({
                                        ...fieldForm,
                                        conditional_constraints: {
                                          ...fieldForm.conditional_constraints!,
                                          rules: newRules,
                                        },
                                      });
                                    }
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setFieldForm({
                              ...fieldForm,
                              conditional_constraints: {
                                ...fieldForm.conditional_constraints!,
                                rules: [
                                  ...fieldForm.conditional_constraints!.rules,
                                  { when_value: "", ...(fieldForm.field_type === "number" ? { min_value: undefined, max_value: undefined } : { allowed_values: [] }) },
                                ],
                              },
                            })
                          }
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Ajouter une règle
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Label Style Section */}
            <div className="col-span-2 space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 7 4 4 20 4 20 7" />
                    <line x1="9" x2="15" y1="20" y2="20" />
                    <line x1="12" x2="12" y1="4" y2="20" />
                  </svg>
                  <Label>Style du libellé</Label>
                </div>
                {!fieldForm.label_style ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFieldForm({
                        ...fieldForm,
                        label_style: {},
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Personnaliser
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFieldForm({ ...fieldForm, label_style: null })}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Réinitialiser
                  </Button>
                )}
              </div>

              {fieldForm.label_style && (
                <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                  {/* Preview */}
                  <div className="p-3 rounded border bg-background">
                    <p className="text-xs text-muted-foreground mb-1">Aperçu :</p>
                    <span
                      className={cn(
                        "text-sm font-medium leading-none",
                        fieldForm.label_style.fontSize && `text-${fieldForm.label_style.fontSize}`,
                        fieldForm.label_style.fontWeight && `font-${fieldForm.label_style.fontWeight}`,
                        fieldForm.label_style.fontStyle === "italic" && "italic",
                        fieldForm.label_style.textTransform && fieldForm.label_style.textTransform !== "none" && fieldForm.label_style.textTransform,
                        fieldForm.label_style.color === "primary" && "text-primary",
                        fieldForm.label_style.color === "secondary" && "text-secondary-foreground",
                        fieldForm.label_style.color === "muted" && "text-muted-foreground",
                        fieldForm.label_style.color === "destructive" && "text-destructive",
                        fieldForm.label_style.customClassName
                      )}
                      style={fieldForm.label_style.color === "custom" && fieldForm.label_style.customColor ? { color: fieldForm.label_style.customColor } : undefined}
                    >
                      {parseBBCode(fieldForm.label || "Libellé du champ")}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Font Size */}
                    <div className="space-y-2">
                      <Label htmlFor="label-font-size">Taille</Label>
                      <Select
                        value={fieldForm.label_style.fontSize || "default"}
                        onValueChange={(value) =>
                          setFieldForm({
                            ...fieldForm,
                            label_style: {
                              ...fieldForm.label_style!,
                              fontSize: value === "default" ? undefined : value as LabelFontSize,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="xs">Très petit (xs)</SelectItem>
                          <SelectItem value="sm">Petit (sm)</SelectItem>
                          <SelectItem value="base">Normal (base)</SelectItem>
                          <SelectItem value="lg">Grand (lg)</SelectItem>
                          <SelectItem value="xl">Très grand (xl)</SelectItem>
                          <SelectItem value="2xl">Extra grand (2xl)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Font Weight */}
                    <div className="space-y-2">
                      <Label htmlFor="label-font-weight">Poids</Label>
                      <Select
                        value={fieldForm.label_style.fontWeight || "default"}
                        onValueChange={(value) =>
                          setFieldForm({
                            ...fieldForm,
                            label_style: {
                              ...fieldForm.label_style!,
                              fontWeight: value === "default" ? undefined : value as LabelFontWeight,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="medium">Moyen</SelectItem>
                          <SelectItem value="semibold">Semi-gras</SelectItem>
                          <SelectItem value="bold">Gras</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Font Style */}
                    <div className="space-y-2">
                      <Label htmlFor="label-font-style">Style</Label>
                      <Select
                        value={fieldForm.label_style.fontStyle || "default"}
                        onValueChange={(value) =>
                          setFieldForm({
                            ...fieldForm,
                            label_style: {
                              ...fieldForm.label_style!,
                              fontStyle: value === "default" ? undefined : value as LabelFontStyle,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="italic">Italique</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Text Transform */}
                    <div className="space-y-2">
                      <Label htmlFor="label-text-transform">Casse</Label>
                      <Select
                        value={fieldForm.label_style.textTransform || "default"}
                        onValueChange={(value) =>
                          setFieldForm({
                            ...fieldForm,
                            label_style: {
                              ...fieldForm.label_style!,
                              textTransform: value === "default" ? undefined : value as LabelTextTransform,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="none">Normal</SelectItem>
                          <SelectItem value="uppercase">MAJUSCULES</SelectItem>
                          <SelectItem value="lowercase">minuscules</SelectItem>
                          <SelectItem value="capitalize">Première Lettre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Color */}
                    <div className="space-y-2">
                      <Label htmlFor="label-color">Couleur</Label>
                      <Select
                        value={fieldForm.label_style.color || "default"}
                        onValueChange={(value) =>
                          setFieldForm({
                            ...fieldForm,
                            label_style: {
                              ...fieldForm.label_style!,
                              color: value === "default" ? undefined : value as LabelColor,
                              customColor: value !== "custom" ? undefined : fieldForm.label_style?.customColor,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="primary">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-primary" />
                              Principal
                            </span>
                          </SelectItem>
                          <SelectItem value="secondary">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-secondary" />
                              Secondaire
                            </span>
                          </SelectItem>
                          <SelectItem value="muted">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-muted-foreground" />
                              Atténué
                            </span>
                          </SelectItem>
                          <SelectItem value="destructive">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full bg-destructive" />
                              Destructif
                            </span>
                          </SelectItem>
                          <SelectItem value="custom">Personnalisée...</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Custom Color Input */}
                    {fieldForm.label_style.color === "custom" && (
                      <div className="space-y-2">
                        <Label htmlFor="label-custom-color">Couleur personnalisée</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            id="label-custom-color-picker"
                            value={fieldForm.label_style.customColor || "#000000"}
                            onChange={(e) =>
                              setFieldForm({
                                ...fieldForm,
                                label_style: {
                                  ...fieldForm.label_style!,
                                  customColor: e.target.value,
                                },
                              })
                            }
                            className="w-12 h-9 p-1 cursor-pointer"
                          />
                          <Input
                            id="label-custom-color"
                            value={fieldForm.label_style.customColor || ""}
                            onChange={(e) =>
                              setFieldForm({
                                ...fieldForm,
                                label_style: {
                                  ...fieldForm.label_style!,
                                  customColor: e.target.value,
                                },
                              })
                            }
                            placeholder="#000000"
                            className="font-mono"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Advanced: Custom Tailwind Classes */}
                  <div className="space-y-2 pt-3 border-t">
                    <Label htmlFor="label-custom-class" className="text-xs text-muted-foreground">
                      Classes Tailwind personnalisées (avancé)
                    </Label>
                    <Input
                      id="label-custom-class"
                      value={fieldForm.label_style.customClassName || ""}
                      onChange={(e) =>
                        setFieldForm({
                          ...fieldForm,
                          label_style: {
                            ...fieldForm.label_style!,
                            customClassName: e.target.value || undefined,
                          },
                        })
                      }
                      placeholder="Ex: tracking-wide underline"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeFieldDialog}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                isEditingField && selectedField
                  ? updateFieldMutation.mutate({ id: selectedField.id, data: fieldForm })
                  : createFieldMutation.mutate(fieldForm)
              }
              disabled={
                createFieldMutation.isPending || 
                updateFieldMutation.isPending || 
                (!createNewGroupInFieldDialog && !fieldForm.group_id) ||
                (createNewGroupInFieldDialog && !newGroupInFieldDialog.name)
              }
            >
              {isEditingField ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Template Dialog */}
      <Dialog open={importTemplateDialogOpen} onOpenChange={setImportTemplateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutTemplate className="h-5 w-5 text-primary" />
              Importer un modèle
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un modèle à importer. Les champs seront copiés dans cette version.
            </DialogDescription>
          </DialogHeader>
          
          <Accordion type="single" collapsible className="w-full" defaultValue="groups">
            <AccordionItem value="groups">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-4 w-4 text-laposte-navy" />
                  <span>Modèles de groupes</span>
                  {groupTemplates && (
                    <Badge variant="secondary" className="ml-2">
                      {groupTemplates.length}
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {!groupTemplates || groupTemplates.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucun modèle de groupe disponible
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {groupTemplates.map((template) => {
                      const fieldCount = template.fields.length + template.children.reduce((acc, c) => acc + c.fields.length, 0);
                      return (
                        <div
                          key={template.id}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedTemplateGroup?.id === template.id
                              ? "border-primary bg-primary/5"
                              : "hover:bg-muted/50"
                          }`}
                          onClick={() => {
                            setSelectedTemplateGroup(template);
                            setSelectedTemplateField(null);
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-laposte-navy/10 flex items-center justify-center">
                              <FolderOpen className="h-5 w-5 text-laposte-navy" />
                            </div>
                            <div>
                              <p className="font-medium">{template.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {fieldCount} champ{fieldCount !== 1 ? "s" : ""}
                                {template.children.length > 0 && ` • ${template.children.length} sous-groupe${template.children.length !== 1 ? "s" : ""}`}
                              </p>
                            </div>
                          </div>
                          {selectedTemplateGroup?.id === template.id && (
                            <Check className="h-5 w-5 text-primary" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="fields">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <FormInput className="h-4 w-4 text-info" />
                  <span>Modèles de champs individuels</span>
                  {fieldTemplates && (
                    <Badge variant="secondary" className="ml-2">
                      {fieldTemplates.total_items}
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {!fieldTemplates || fieldTemplates.items.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucun modèle de champ disponible
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                      {fieldTemplates.items.map((template) => (
                        <div
                          key={template.id}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedTemplateField?.id === template.id
                              ? "border-primary bg-primary/5"
                              : "hover:bg-muted/50"
                          }`}
                          onClick={() => {
                            setSelectedTemplateField(template);
                            setSelectedTemplateGroup(null);
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-info/10 flex items-center justify-center">
                              <FormInput className="h-5 w-5 text-info" />
                            </div>
                            <div>
                              <p className="font-medium">{template.label}</p>
                              <p className="text-xs text-muted-foreground">{template.slug}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{template.field_type}</Badge>
                            {selectedTemplateField?.id === template.id && (
                              <Check className="h-5 w-5 text-primary" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {selectedTemplateField && groups && groups.length > 0 && (
                      <div className="pt-3 border-t">
                        <Label className="text-sm">Groupe de destination</Label>
                        <Select
                          value={targetGroupIdForFieldImport?.toString() || ""}
                          onValueChange={(v) => setTargetGroupIdForFieldImport(Number(v))}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Sélectionner un groupe..." />
                          </SelectTrigger>
                          <SelectContent>
                            {groups.map((g) => (
                              <SelectItem key={g.id} value={g.id.toString()}>
                                {g.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setImportTemplateDialogOpen(false);
                setSelectedTemplateGroup(null);
                setSelectedTemplateField(null);
                setTargetGroupIdForFieldImport(null);
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (selectedTemplateGroup) {
                  importGroupTemplateMutation.mutate({ templateId: selectedTemplateGroup.id });
                } else if (selectedTemplateField && targetGroupIdForFieldImport) {
                  importFieldTemplateMutation.mutate({
                    templateId: selectedTemplateField.id,
                    targetGroupId: targetGroupIdForFieldImport,
                  });
                }
              }}
              disabled={
                importGroupTemplateMutation.isPending ||
                importFieldTemplateMutation.isPending ||
                (!selectedTemplateGroup && (!selectedTemplateField || !targetGroupIdForFieldImport))
              }
            >
              {importGroupTemplateMutation.isPending || importFieldTemplateMutation.isPending ? (
                "Import en cours..."
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Importer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </Card>
  );
}
